// Sorriso Planejado — V10 nomenclatura Captador padronizada na interface, preservando V9 — 01/10/2026
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  HandCoins,
  ListChecks,
  ListTodo,
  MessageCircle,
  PhoneCall,
  PencilLine,
  PlusCircle,
  Printer,
  RefreshCcw,
  Search,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import { api } from "../api";

function norm(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function ehRegistroCobrancaSorriso(item) {
  return (
    norm(item?.origem_modulo) === "sorriso_planejado" &&
    norm(item?.categoria) === "cobranca"
  );
}

function ehInteracaoHistoricoSorriso(item) {
  return (
    norm(item?.origem_modulo) === "sorriso_planejado" &&
    ["cobranca", "implantacao", "cancelamento", "correcao"].includes(norm(item?.categoria))
  );
}

function contextoCobrancaSorriso(lead, parcela = null) {
  const parcelaId = Number(parcela?.id || 0);
  if (Number.isInteger(parcelaId) && parcelaId > 0) {
    return {
      origem_modulo: "sorriso_planejado",
      categoria: "cobranca",
      referencia_tipo: "parcela",
      referencia_id: parcelaId,
    };
  }

  const contratoId = Number(parcela?.contrato_id || 0);
  if (Number.isInteger(contratoId) && contratoId > 0) {
    return {
      origem_modulo: "sorriso_planejado",
      categoria: "cobranca",
      referencia_tipo: "contrato",
      referencia_id: contratoId,
    };
  }

  return {
    origem_modulo: "sorriso_planejado",
    categoria: "cobranca",
    referencia_tipo: "lead",
    referencia_id: Number(lead?.id || 0) || null,
  };
}

function moeda(value) {
  const numero = Number(value);
  if (!Number.isFinite(numero)) return "—";
  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function valorTotalContratoSorriso(item = {}) {
  const parcelas = Array.isArray(item?.parcelas) ? item.parcelas : [];
  if (!parcelas.length) return 0;

  const valorBackend = Number(parcelas?.[0]?.valor_total_contrato || 0);
  if (valorBackend > 0) return Number(valorBackend.toFixed(2));

  const totalParcelas = Number(
    parcelas?.[0]?.contrato_total_parcelas ||
      parcelas?.[0]?.total_parcelas ||
      0,
  );
  const valorPadrao = Number(
    parcelas?.[0]?.valor_boleto_padrao ??
      parcelas?.[0]?.valor_boleto ??
      parcelas?.[0]?.valor ??
      0,
  );

  const numeros = new Set(
    parcelas
      .map((p) => Number(p?.numero_parcela || 0))
      .filter((n) => Number.isInteger(n) && n > 0),
  );
  const somaRegistrada = parcelas.reduce(
    (soma, p) => soma + Number(p?.valor ?? p?.valor_boleto ?? 0),
    0,
  );

  // Contrato completo no módulo: usa os valores reais de cada parcela.
  if (totalParcelas > 0 && numeros.size >= totalParcelas) {
    return Number(somaRegistrada.toFixed(2));
  }

  // Contrato antigo/importado com apenas parcelas remanescentes: o modelo
  // registra um valor padrão por boleto e o total original de parcelas.
  if (totalParcelas > 0 && valorPadrao > 0) {
    return Number((totalParcelas * valorPadrao).toFixed(2));
  }

  return Number(somaRegistrada.toFixed(2));
}

function nomeUsuario(id, usuarios = []) {
  const usuario = usuarios.find((item) => String(item?.id) === String(id));
  return usuario?.nome || "—";
}

function nomeDentista(id, dentistas = []) {
  const dentista = dentistas.find((item) => String(item?.id) === String(id));
  return dentista?.profissional || dentista?.nome || "—";
}

function getResponsavelId(lead) {
  // O campo interno responsavel_id representa o captador responsável pela carteira.
  return lead?.sorriso_responsavel_id || lead?.usuario_criador_id || lead?.id_usuario || null;
}

function isGestor(usuario = {}) {
  const cargo = norm(
    usuario?.cargo ||
      usuario?.perfil ||
      usuario?.role ||
      usuario?.tipo ||
      usuario?.tipo_usuario ||
      usuario?.nivel_acesso,
  );

  return (
    cargo.includes("admin") ||
    cargo.includes("administrador") ||
    cargo.includes("supervisor")
  );
}

function dataYmd(value) {
  if (!value) return "";
  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function hojeYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function formatarData(value) {
  const ymd = dataYmd(value);
  if (!ymd) return "—";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function formatarDataHora(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return formatarData(value);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const MOTIVOS_CANCELAMENTO = [
  ["DESISTENCIA", "Desistência do paciente"],
  ["DIFICULDADE_FINANCEIRA", "Dificuldade financeira"],
  ["NAO_ENTENDEU_PLANO", "Não entendeu / não se adaptou ao plano"],
  ["MUDANCA_TRATAMENTO", "Mudança de tratamento"],
  ["TRANSFERENCIA", "Transferência"],
  ["INSATISFACAO", "Insatisfação"],
  ["ACORDO_CLINICA", "Acordo com a clínica"],
  ["OUTRO", "Outro"],
];

function rotuloMotivoCancelamento(value) {
  return MOTIVOS_CANCELAMENTO.find(([codigo]) => codigo === String(value || "").toUpperCase())?.[1] || value || "—";
}

function rotuloClassificacaoCancelamento(value) {
  return String(value || "").toUpperCase() === "SEM_PAGAMENTO"
    ? "Cancelado sem pagamento"
    : String(value || "").toUpperCase() === "APOS_INICIO"
      ? "Cancelado após início"
      : "Cancelado";
}

function diasEntre(a, b) {
  const da = dataYmd(a);
  const db = dataYmd(b);
  if (!da || !db) return null;
  const [ya, ma, dda] = da.split("-").map(Number);
  const [yb, mb, ddb] = db.split("-").map(Number);
  return Math.round(
    (Date.UTC(yb, mb - 1, ddb) - Date.UTC(ya, ma - 1, dda)) / 86400000,
  );
}

function mesAtualYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatarMes(value) {
  if (!/^\d{4}-\d{2}$/.test(String(value || ""))) return value || "";
  const [ano, mes] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(new Date(ano, mes - 1, 1));
}

function formatarMesCurto(value) {
  if (!/^\d{4}-\d{2}$/.test(String(value || ""))) return value || "";
  const [ano, mes] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("pt-BR", {
    month: "short",
    year: "2-digit",
  })
    .format(new Date(ano, mes - 1, 1))
    .replace(".", "");
}

function ultimosMeses(quantidade = 8) {
  const agora = new Date();
  const meses = [];
  for (let i = quantidade - 1; i >= 0; i -= 1) {
    const data = new Date(agora.getFullYear(), agora.getMonth() - i, 1);
    meses.push(`${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`);
  }
  return meses;
}

function tendenciaPercentual(atual, anterior, inversa = false) {
  const valorAtual = Number(atual || 0);
  const valorAnterior = Number(anterior || 0);

  if (valorAnterior > 0) {
    const variacao = ((valorAtual - valorAnterior) / valorAnterior) * 100;
    const arredondada = Number(variacao.toFixed(1));
    const estavel = Math.abs(arredondada) < 0.1;
    const subiu = arredondada > 0;
    const caiu = arredondada < 0;
    const favoravel = estavel ? null : inversa ? caiu : subiu;

    return {
      tipo: estavel ? "estavel" : subiu ? "alta" : "queda",
      simbolo: estavel ? "→" : subiu ? "↑" : "↓",
      percentual: Math.abs(arredondada),
      texto: `${Math.abs(arredondada).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`,
      classe:
        favoravel === true
          ? "text-emerald-700"
          : favoravel === false
            ? "text-rose-700"
            : "text-slate-500",
      fundo:
        favoravel === true
          ? "bg-emerald-50 border-emerald-200"
          : favoravel === false
            ? "bg-rose-50 border-rose-200"
            : "bg-slate-50 border-slate-200",
    };
  }

  if (valorAtual > 0) {
    const favoravel = inversa ? false : true;
    return {
      tipo: "novo",
      simbolo: "↑",
      percentual: null,
      texto: "Novo",
      classe: favoravel ? "text-emerald-700" : "text-rose-700",
      fundo: favoravel
        ? "bg-emerald-50 border-emerald-200"
        : "bg-rose-50 border-rose-200",
    };
  }

  return {
    tipo: "estavel",
    simbolo: "→",
    percentual: 0,
    texto: "0,0%",
    classe: "text-slate-500",
    fundo: "bg-slate-50 border-slate-200",
  };
}

function numeroPositivo(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function valorDecimalPositivo(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dataEvento(item = {}) {
  return (
    item?.criado_em ||
    item?.created_at ||
    item?.data_interacao ||
    item?.data_criacao ||
    item?.data ||
    item?.data_vencimento ||
    null
  );
}

function textoEvento(item = {}) {
  return (
    item?.descricao ||
    item?.observacao ||
    item?.titulo ||
    item?.tipo_interacao ||
    item?.tipo ||
    item?.status ||
    "Registro"
  );
}

function CardKpi({ icon, valor, titulo, detalhe, tone = "blue" }) {
  const tones = {
    blue: { accent: "bg-blue-600", icon: "bg-blue-50 text-blue-700 ring-blue-100" },
    green: { accent: "bg-emerald-500", icon: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
    amber: { accent: "bg-amber-500", icon: "bg-amber-50 text-amber-700 ring-amber-100" },
    red: { accent: "bg-rose-500", icon: "bg-rose-50 text-rose-700 ring-rose-100" },
    violet: { accent: "bg-violet-500", icon: "bg-violet-50 text-violet-700 ring-violet-100" },
    slate: { accent: "bg-slate-500", icon: "bg-slate-100 text-slate-700 ring-slate-200" },
  };
  const visual = tones[tone] || tones.blue;

  return (
    <div className="relative min-h-[108px] overflow-hidden rounded-xl border border-slate-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition hover:border-slate-300 hover:shadow-[0_4px_12px_rgba(15,23,42,0.06)]">
      <span className={`absolute inset-y-0 left-0 w-[3px] ${visual.accent}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 pl-1">
          <div className="truncate text-[11px] font-semibold uppercase tracking-[0.02em] text-slate-500">{titulo}</div>
          <div className="mt-1.5 truncate text-[20px] font-semibold leading-none tracking-tight text-slate-900">{valor}</div>
        </div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${visual.icon}`}>
          {icon}
        </div>
      </div>
      <div className="mt-3 border-t border-slate-100 pt-2 text-[11px] leading-tight text-slate-500">{detalhe}</div>
    </div>
  );
}

function BadgeStatus({ status }) {
  const mapa = {
    aguardando: "border-amber-200 bg-amber-50 text-amber-700",
    atrasada: "border-rose-200 bg-rose-50 text-rose-700",
    hoje: "border-amber-200 bg-amber-100 text-amber-700",
    proximo: "border-blue-200 bg-blue-50 text-blue-700",
    pago: "border-emerald-200 bg-emerald-50 text-emerald-700",
    cancelado: "border-slate-300 bg-slate-100 text-slate-700",
    avencer: "border-sky-200 bg-sky-50 text-sky-700",
  };

  const rotulo = {
    aguardando: "Aguardando parcelas",
    atrasada: "Atrasada",
    hoje: "Vence hoje",
    proximo: "Próxima",
    pago: "Paga",
    cancelado: "Cancelado",
    avencer: "A vencer",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium ${
        mapa[status] || mapa.avencer
      }`}
    >
      {rotulo[status] || "A vencer"}
    </span>
  );
}

export default function SorrisoPlanejado({
  leads = [],
  campanhas = [],
  usuarios = [],
  dentistas = [],
  tarefas = [],
  interacoes = [],
  parcelas = [],
  usuarioLogado = {},
  empresaAtiva = null,
  carregando = false,
  onVoltar,
  onAtualizar,
  onNovaLead,
  onWhatsApp,
  onNovaTarefa,
  onInteracoes,
  onLancarParcelas,
  onAbrirLead,
}) {
  const gestor = isGestor(usuarioLogado);
  const [busca, setBusca] = useState("");
  const [responsavelFiltro, setResponsavelFiltro] = useState("");
  const [dentistaFiltro, setDentistaFiltro] = useState("");
  const [situacaoFiltro, setSituacaoFiltro] = useState("todos");
  const [mesReferencia, setMesReferencia] = useState(mesAtualYmd());
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(20);
  const [visaoPerformance, setVisaoPerformance] = useState("fluxo");
  const [leadSelecionadaId, setLeadSelecionadaId] = useState(null);
  const [idsSelecionados, setIdsSelecionados] = useState(() => new Set());
  const [abaDetalhe, setAbaDetalhe] = useState("resumo");
  const [parcelasApi, setParcelasApi] = useState([]);
  const [leadsComContratoApi, setLeadsComContratoApi] = useState(() => new Set());
  const [parcelasApiCarregadas, setParcelasApiCarregadas] = useState(false);
  const [carregandoParcelasApi, setCarregandoParcelasApi] = useState(false);
  const [modalParcelasLead, setModalParcelasLead] = useState(null);
  const [salvandoParcelas, setSalvandoParcelas] = useState(false);
  const [modalBaixa, setModalBaixa] = useState(null);
  const [dataBaixa, setDataBaixa] = useState(hojeYmd());
  const [salvandoBaixa, setSalvandoBaixa] = useState(false);
  const [modalCancelamento, setModalCancelamento] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [descricaoCancelamento, setDescricaoCancelamento] = useState("");
  const [salvandoCancelamento, setSalvandoCancelamento] = useState(false);
  const [modalCorrecao, setModalCorrecao] = useState(null);
  const [motivoCorrecao, setMotivoCorrecao] = useState("");
  const [salvandoCorrecao, setSalvandoCorrecao] = useState(false);
  const [formCorrecao, setFormCorrecao] = useState({
    total_parcelas: "",
    parcela_inicial: "",
    quantidade_parcelas: "",
    valor_boleto: "",
    primeiro_vencimento: "",
    responsavel_id: "",
  });
  const [tipoContrato, setTipoContrato] = useState("novo");
  const [formParcelas, setFormParcelas] = useState({
    total_parcelas: "",
    parcela_inicial: "1",
    quantidade_parcelas: "",
    valor_boleto: "",
    primeiro_vencimento: "",
    responsavel_id: "",
  });

  const carregarParcelasApi = useCallback(async () => {
    const empresaId = empresaAtiva?.id;
    if (!empresaId) {
      setParcelasApi([]);
      setLeadsComContratoApi(new Set());
      setParcelasApiCarregadas(false);
      return;
    }

    setCarregandoParcelasApi(true);
    try {
      const response = await api.get("/sorriso-planejado/parcelas", {
        params: { empresa_id: empresaId },
      });
      const data = response?.data ?? response;
      const lista = Array.isArray(data?.parcelas)
        ? data.parcelas
        : Array.isArray(data)
          ? data
          : [];
      setParcelasApi(lista);
      setLeadsComContratoApi(
        new Set((Array.isArray(data?.leads_com_contrato) ? data.leads_com_contrato : []).map((id) => String(id))),
      );
      setParcelasApiCarregadas(true);
    } catch (error) {
      console.error("❌ Sorriso Planejado - carregar parcelas:", error);
      setParcelasApi([]);
      setLeadsComContratoApi(new Set());
      setParcelasApiCarregadas(false);
    } finally {
      setCarregandoParcelasApi(false);
    }
  }, [empresaAtiva?.id]);

  useEffect(() => {
    carregarParcelasApi();
  }, [carregarParcelasApi]);

  const parcelasEfetivas = parcelasApiCarregadas ? parcelasApi : parcelas;

  const abrirLancamentoParcelas = useCallback(
    (lead) => {
      const responsavelPadrao = gestor
        ? getResponsavelId(lead) || usuarioLogado?.id || ""
        : usuarioLogado?.id || "";

      setTipoContrato("novo");
      setFormParcelas({
        total_parcelas: "",
        parcela_inicial: "1",
        quantidade_parcelas: "",
        valor_boleto: "",
        primeiro_vencimento: "",
        responsavel_id: String(responsavelPadrao || ""),
      });
      setModalParcelasLead(lead);
    },
    [gestor, usuarioLogado?.id],
  );

  const salvarLancamentoParcelas = useCallback(async () => {
    const lead = modalParcelasLead;
    const empresaId = empresaAtiva?.id || lead?.empresa_id;
    if (!lead?.id || !empresaId) {
      toast.error("Não foi possível identificar a lead ou empresa.");
      return;
    }

    const quantidade = numeroPositivo(formParcelas.quantidade_parcelas);
    const total =
      tipoContrato === "novo"
        ? quantidade
        : numeroPositivo(formParcelas.total_parcelas);
    const inicial =
      tipoContrato === "novo"
        ? 1
        : numeroPositivo(formParcelas.parcela_inicial);
    const valor = valorDecimalPositivo(formParcelas.valor_boleto);
    const responsavelId = numeroPositivo(formParcelas.responsavel_id);

    if (!quantidade || !total || !inicial || !valor || !formParcelas.primeiro_vencimento || !responsavelId) {
      toast.warning("Preencha parcelas, valor do boleto, vencimento e captador.");
      return;
    }

    if (inicial + quantidade - 1 > total) {
      toast.warning("As parcelas restantes ultrapassam o total original do contrato.");
      return;
    }

    setSalvandoParcelas(true);
    try {
      await api.post("/sorriso-planejado/contratos", {
        empresa_id: Number(empresaId),
        lead_id: Number(lead.id),
        responsavel_id: responsavelId,
        total_parcelas: total,
        parcela_inicial: inicial,
        quantidade_parcelas: quantidade,
        valor_boleto: valor,
        primeiro_vencimento: formParcelas.primeiro_vencimento,
      });

      toast.success("Parcelas do Sorriso Planejado lançadas com sucesso.");
      setModalParcelasLead(null);
      await carregarParcelasApi();
      await onAtualizar?.();
    } catch (error) {
      const dadosErro = error?.response?.data || {};
      const msgBase =
        dadosErro?.erro ||
        dadosErro?.error ||
        error?.message ||
        "Erro ao lançar parcelas.";
      const detalhe = dadosErro?.detalhe ? String(dadosErro.detalhe) : "";
      const etapa = dadosErro?.etapa ? String(dadosErro.etapa) : "";

      const complemento = [etapa ? `etapa: ${etapa}` : "", detalhe]
        .filter(Boolean)
        .join(" · ");

      toast.error(complemento ? `${msgBase} — ${complemento}` : msgBase, {
        autoClose: 12000,
      });
    } finally {
      setSalvandoParcelas(false);
    }
  }, [
    modalParcelasLead,
    empresaAtiva?.id,
    formParcelas,
    tipoContrato,
    carregarParcelasApi,
    onAtualizar,
  ]);

  const abrirBaixaParcela = useCallback((lead, parcela) => {
    if (!lead?.id || !parcela?.id) {
      toast.error("Não foi possível identificar a parcela para baixa.");
      return;
    }

    if (norm(parcela?.status) === "paga" || parcela?.receb_lead_id) {
      toast.info("Esta parcela já está paga.");
      return;
    }

    setDataBaixa(hojeYmd());
    setModalBaixa({ lead, parcela });
  }, []);

  const salvarBaixaParcela = useCallback(async () => {
    const lead = modalBaixa?.lead;
    const parcela = modalBaixa?.parcela;
    const empresaId = empresaAtiva?.id || lead?.empresa_id || parcela?.empresa_id;
    const dataRecebimento = dataYmd(dataBaixa);

    if (!lead?.id || !parcela?.id || !empresaId) {
      toast.error("Não foi possível identificar a parcela, lead ou empresa.");
      return;
    }

    if (!dataRecebimento) {
      toast.warning("Informe a data do recebimento.");
      return;
    }

    setSalvandoBaixa(true);
    try {
      const response = await api.post(
        `/sorriso-planejado/parcelas/${parcela.id}/baixa`,
        {
          empresa_id: Number(empresaId),
          data_recebimento: dataRecebimento,
        },
      );

      const data = response?.data ?? response;
      toast.success(
        data?.recebimento?.id
          ? `Parcela baixada e recebimento #${data.recebimento.id} criado.`
          : "Parcela baixada e recebimento registrado com sucesso.",
      );
      setModalBaixa(null);
      await carregarParcelasApi();
      await onAtualizar?.();
    } catch (error) {
      const msg =
        error?.response?.data?.erro ||
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao dar baixa na parcela.";
      toast.error(msg);
    } finally {
      setSalvandoBaixa(false);
    }
  }, [modalBaixa, empresaAtiva?.id, dataBaixa, carregarParcelasApi, onAtualizar]);

  const abrirCorrecaoLancamento = useCallback((item) => {
    const contratoId = numeroPositivo(item?.parcelas?.[0]?.contrato_id);

    if (!gestor) {
      toast.error("Somente Administrador ou Supervisor pode corrigir o lançamento.");
      return;
    }
    if (!item?.lead?.id || !contratoId || !item?.parcelas?.length) {
      toast.error("Não foi possível identificar o contrato para correção.");
      return;
    }
    if (item?.status === "cancelado") {
      toast.info("Contrato cancelado não pode ser corrigido.");
      return;
    }
    if (item?.status === "pago") {
      toast.info("Este contrato está quitado e não possui parcelas futuras para corrigir.");
      return;
    }

    const pagasOrdenadas = [...(item?.pagas || [])].sort(
      (a, b) => Number(a?.numero_parcela || 0) - Number(b?.numero_parcela || 0),
    );
    const futurasOrdenadas = [...(item?.abertas || [])].sort(
      (a, b) => Number(a?.numero_parcela || 0) - Number(b?.numero_parcela || 0),
    );
    const referencia = futurasOrdenadas[0] || item.parcelas[0];
    const totalAtual = Number(
      referencia?.total_parcelas ||
        item.parcelas?.[0]?.total_parcelas ||
        item.parcelas.length,
    );
    const maxPaga = pagasOrdenadas.length
      ? Math.max(...pagasOrdenadas.map((p) => Number(p?.numero_parcela || 0)))
      : 0;
    const inicial =
      pagasOrdenadas.length > 0
        ? maxPaga + 1
        : Number(futurasOrdenadas[0]?.numero_parcela || referencia?.numero_parcela || 1);

    setMotivoCorrecao("");
    setFormCorrecao({
      total_parcelas: String(totalAtual || ""),
      parcela_inicial: String(inicial || 1),
      quantidade_parcelas: String(
        futurasOrdenadas.length || Math.max(1, totalAtual - inicial + 1),
      ),
      valor_boleto: String(
        Number(futurasOrdenadas[0]?.valor ?? referencia?.valor ?? referencia?.valor_boleto ?? 0) || "",
      ),
      primeiro_vencimento: dataYmd(
        futurasOrdenadas[0]?.vencimento ||
          futurasOrdenadas[0]?.data_vencimento ||
          referencia?.vencimento ||
          referencia?.data_vencimento,
      ),
      responsavel_id: String(item?.responsavelId || usuarioLogado?.id || ""),
    });
    setModalCorrecao({
      item,
      contratoId,
      parcelasPagas: pagasOrdenadas.length,
      maxParcelaPaga: maxPaga,
      parcelasFuturasAtuais: futurasOrdenadas.length,
      valorFuturoAtual: futurasOrdenadas.reduce(
        (soma, p) => soma + Number(p?.valor ?? p?.valor_boleto ?? 0),
        0,
      ),
    });
  }, [gestor, usuarioLogado?.id]);

  const salvarCorrecaoLancamento = useCallback(async () => {
    const item = modalCorrecao?.item;
    const contratoId = numeroPositivo(modalCorrecao?.contratoId);
    const empresaId = empresaAtiva?.id || item?.lead?.empresa_id;
    const total = numeroPositivo(formCorrecao.total_parcelas);
    const inicial = numeroPositivo(formCorrecao.parcela_inicial);
    const quantidade = numeroPositivo(formCorrecao.quantidade_parcelas);
    const valor = valorDecimalPositivo(formCorrecao.valor_boleto);
    const responsavelId = numeroPositivo(formCorrecao.responsavel_id);
    const primeiroVencimento = dataYmd(formCorrecao.primeiro_vencimento);
    const motivo = String(motivoCorrecao || "").trim();

    if (!item?.lead?.id || !contratoId || !empresaId) {
      toast.error("Não foi possível identificar o contrato, a lead ou a empresa.");
      return;
    }

    if (!total || !inicial || !quantidade || !valor || !responsavelId || !primeiroVencimento) {
      toast.warning("Preencha corretamente os dados da correção.");
      return;
    }

    if (inicial + quantidade - 1 > total) {
      toast.warning("As parcelas corrigidas ultrapassam o total informado.");
      return;
    }

    if (motivo.length < 30) {
      toast.warning("Explique o motivo da correção com pelo menos 30 caracteres.");
      return;
    }

    setSalvandoCorrecao(true);
    try {
      const response = await api.post(
        `/sorriso-planejado/contratos/${contratoId}/corrigir`,
        {
          empresa_id: Number(empresaId),
          responsavel_id: responsavelId,
          total_parcelas: total,
          parcela_inicial: inicial,
          quantidade_parcelas: quantidade,
          valor_boleto: valor,
          primeiro_vencimento: primeiroVencimento,
          motivo,
        },
      );

      const data = response?.data ?? response;
      toast.success(data?.mensagem || "Lançamento corrigido com sucesso.");
      setModalCorrecao(null);
      setMotivoCorrecao("");
      await carregarParcelasApi();
      await onAtualizar?.();
    } catch (error) {
      const dadosErro = error?.response?.data || {};
      const msgBase =
        dadosErro?.erro ||
        dadosErro?.error ||
        error?.message ||
        "Erro ao corrigir o lançamento.";
      const detalhe = dadosErro?.detalhe ? String(dadosErro.detalhe) : "";
      const etapa = dadosErro?.etapa ? String(dadosErro.etapa) : "";
      const complemento = [etapa ? `etapa: ${etapa}` : "", detalhe]
        .filter(Boolean)
        .join(" · ");

      toast.error(complemento ? `${msgBase} — ${complemento}` : msgBase, {
        autoClose: 12000,
      });
    } finally {
      setSalvandoCorrecao(false);
    }
  }, [
    modalCorrecao,
    empresaAtiva?.id,
    formCorrecao,
    motivoCorrecao,
    carregarParcelasApi,
    onAtualizar,
  ]);

  const abrirCancelamento = useCallback((item) => {
    const contratoId = numeroPositivo(item?.parcelas?.[0]?.contrato_id);
    if (!gestor) {
      toast.error("Somente Administrador ou Supervisor pode cancelar o Sorriso Planejado.");
      return;
    }
    if (!item?.lead?.id || !contratoId) {
      toast.error("Não foi possível identificar o contrato para cancelamento.");
      return;
    }
    if (item?.status === "cancelado") {
      toast.info("Este Sorriso Planejado já está cancelado.");
      return;
    }
    if (item?.status === "pago") {
      toast.info("Este contrato já está quitado e não deve ser cancelado.");
      return;
    }

    const valorPago = item.pagas.reduce(
      (soma, parcela) => soma + Number(parcela?.valor ?? parcela?.valor_boleto ?? 0),
      0,
    );
    const valorEncerrado = item.abertas.reduce(
      (soma, parcela) => soma + Number(parcela?.valor ?? parcela?.valor_boleto ?? 0),
      0,
    );

    setMotivoCancelamento("");
    setDescricaoCancelamento("");
    setModalCancelamento({
      item,
      contratoId,
      valorPago,
      valorEncerrado,
      parcelasPagas: item.pagas.length,
      parcelasEncerrar: item.abertas.length,
    });
  }, [gestor]);

  const salvarCancelamento = useCallback(async () => {
    const item = modalCancelamento?.item;
    const contratoId = numeroPositivo(modalCancelamento?.contratoId);
    const empresaId = empresaAtiva?.id || item?.lead?.empresa_id;
    const descricao = String(descricaoCancelamento || "").trim();

    if (!item?.lead?.id || !contratoId || !empresaId) {
      toast.error("Não foi possível identificar o contrato, a lead ou a empresa.");
      return;
    }
    if (!motivoCancelamento) {
      toast.warning("Selecione o motivo do cancelamento.");
      return;
    }
    if (descricao.length < 30) {
      toast.warning("Descreva o cancelamento com pelo menos 30 caracteres.");
      return;
    }

    setSalvandoCancelamento(true);
    try {
      const response = await api.post(
        `/sorriso-planejado/contratos/${contratoId}/cancelar`,
        {
          empresa_id: Number(empresaId),
          motivo: motivoCancelamento,
          descricao,
        },
      );

      const data = response?.data ?? response;
      const classificacao = data?.cancelamento?.classificacao_cancelamento;
      toast.success(
        classificacao
          ? `Sorriso Planejado cancelado: ${rotuloClassificacaoCancelamento(classificacao)}.`
          : "Sorriso Planejado cancelado com sucesso.",
      );
      setModalCancelamento(null);
      setMotivoCancelamento("");
      setDescricaoCancelamento("");
      await carregarParcelasApi();
      await onAtualizar?.();
    } catch (error) {
      const dadosErro = error?.response?.data || {};
      const msgBase =
        dadosErro?.erro ||
        dadosErro?.error ||
        error?.message ||
        "Erro ao cancelar o Sorriso Planejado.";
      const detalhe = dadosErro?.detalhe ? String(dadosErro.detalhe) : "";
      const etapa = dadosErro?.etapa ? String(dadosErro.etapa) : "";
      const complemento = [etapa ? `etapa: ${etapa}` : "", detalhe]
        .filter(Boolean)
        .join(" · ");

      toast.error(complemento ? `${msgBase} — ${complemento}` : msgBase, {
        autoClose: 12000,
      });
    } finally {
      setSalvandoCancelamento(false);
    }
  }, [
    modalCancelamento,
    empresaAtiva?.id,
    motivoCancelamento,
    descricaoCancelamento,
    carregarParcelasApi,
    onAtualizar,
  ]);

  const campanhaSorriso = useMemo(
    () =>
      campanhas.find((campanha) => norm(campanha?.nome) === "sorriso planejado") ||
      null,
    [campanhas],
  );

  const leadsDaCampanha = useMemo(() => {
    if (!campanhaSorriso?.id) return [];
    const empresaId = empresaAtiva?.id;
    const unicas = new Map();

    for (const lead of Array.isArray(leads) ? leads : []) {
      const mesmaCampanha =
        String(lead?.id_campanha || "") === String(campanhaSorriso.id);
      const mesmaEmpresa =
        !empresaId || String(lead?.empresa_id || empresaId) === String(empresaId);
      if (!mesmaCampanha || !mesmaEmpresa || !lead?.id) continue;
      unicas.set(String(lead.id), lead);
    }

    return [...unicas.values()];
  }, [leads, campanhaSorriso?.id, empresaAtiva?.id]);

  const parcelasPorLead = useMemo(() => {
    const map = new Map();
    for (const parcela of Array.isArray(parcelasEfetivas) ? parcelasEfetivas : []) {
      const leadId = String(parcela?.lead_id ?? parcela?.id_lead ?? "");
      if (!leadId) continue;
      if (!map.has(leadId)) map.set(leadId, []);
      map.get(leadId).push(parcela);
    }
    for (const lista of map.values()) {
      lista.sort((a, b) => String(a?.vencimento || "").localeCompare(String(b?.vencimento || "")));
    }
    return map;
  }, [parcelasEfetivas]);

  // No Sorriso Planejado, tarefas e interações representam somente cobrança.
  // Registros de consulta, negociação, pós-venda etc. continuam existindo no CRM,
  // mas não entram no histórico financeiro desta tela.
  const tarefasPorLead = useMemo(() => {
    const map = new Map();
    for (const tarefa of Array.isArray(tarefas) ? tarefas : []) {
      if (!ehRegistroCobrancaSorriso(tarefa)) continue;
      const id = String(tarefa?.lead_id ?? tarefa?.id_lead ?? "");
      if (!id) continue;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(tarefa);
    }
    return map;
  }, [tarefas]);

  const interacoesPorLead = useMemo(() => {
    const map = new Map();
    for (const interacao of Array.isArray(interacoes) ? interacoes : []) {
      if (!ehInteracaoHistoricoSorriso(interacao)) continue;
      const id = String(interacao?.id_lead ?? interacao?.lead_id ?? "");
      if (!id) continue;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(interacao);
    }
    return map;
  }, [interacoes]);

  const dadosLead = useMemo(() => {
    const hoje = hojeYmd();

    return leadsDaCampanha.map((lead) => {
      const lista = parcelasPorLead.get(String(lead.id)) || [];
      const contratoCancelado = lista.some(
        (p) => norm(p?.contrato_status) === "cancelado",
      );
      const registroCancelamento =
        lista.find((p) => p?.cancelamento_id || p?.cancelado_em) || null;

      const pagas = lista.filter(
        (p) => norm(p?.status) === "paga" || p?.receb_lead_id,
      );
      const naoPagas = lista.filter(
        (p) => !(norm(p?.status) === "paga" || p?.receb_lead_id),
      );
      const encerradas = contratoCancelado ? naoPagas : [];
      const abertas = contratoCancelado ? [] : naoPagas;
      const atual = contratoCancelado
        ? null
        : abertas[0] || lista[lista.length - 1] || null;
      const vencimento = contratoCancelado
        ? dataYmd(registroCancelamento?.cancelado_em)
        : dataYmd(atual?.vencimento || atual?.data_vencimento);
      const diff = contratoCancelado
        ? null
        : vencimento
          ? diasEntre(hoje, vencimento)
          : null;
      let status = "aguardando";

      if (contratoCancelado) {
        status = "cancelado";
      } else if (lista.length > 0) {
        if (abertas.length === 0) status = "pago";
        else if (diff !== null && diff < 0) status = "atrasada";
        else if (diff === 0) status = "hoje";
        else if (diff !== null && diff <= 7) status = "proximo";
        else status = "avencer";
      }

      const cancelamento = registroCancelamento
        ? {
            id: registroCancelamento.cancelamento_id || null,
            motivo: registroCancelamento.motivo_cancelamento || null,
            descricao: registroCancelamento.descricao_cancelamento || null,
            classificacao:
              registroCancelamento.classificacao_cancelamento ||
              (pagas.length ? "APOS_INICIO" : "SEM_PAGAMENTO"),
            valorTotalParcelas: Number(
              registroCancelamento.cancelamento_valor_total_parcelas || 0,
            ),
            valorPagoParcelas: Number(
              registroCancelamento.cancelamento_valor_pago_parcelas || 0,
            ),
            saldoEncerrado: Number(
              registroCancelamento.cancelamento_saldo_encerrado || 0,
            ),
            parcelasPagas: Number(
              registroCancelamento.cancelamento_parcelas_pagas ?? pagas.length,
            ),
            parcelasEncerradas: Number(
              registroCancelamento.cancelamento_parcelas_encerradas ??
                encerradas.length,
            ),
            diasAteCancelamento: Number(
              registroCancelamento.dias_ate_cancelamento || 0,
            ),
            responsavelId:
              registroCancelamento.responsavel_id_snapshot || null,
            captadorId: registroCancelamento.captador_id_snapshot || null,
            canceladoPor: registroCancelamento.cancelado_por || null,
            canceladoEm: registroCancelamento.cancelado_em || null,
          }
        : contratoCancelado
          ? {
              classificacao: pagas.length ? "APOS_INICIO" : "SEM_PAGAMENTO",
              valorTotalParcelas: lista.reduce(
                (soma, p) => soma + Number(p?.valor ?? p?.valor_boleto ?? 0),
                0,
              ),
              valorPagoParcelas: pagas.reduce(
                (soma, p) => soma + Number(p?.valor ?? p?.valor_boleto ?? 0),
                0,
              ),
              saldoEncerrado: encerradas.reduce(
                (soma, p) => soma + Number(p?.valor ?? p?.valor_boleto ?? 0),
                0,
              ),
              parcelasPagas: pagas.length,
              parcelasEncerradas: encerradas.length,
            }
          : null;

      const listaInteracoes = interacoesPorLead.get(String(lead.id)) || [];
      const ultimaInteracao = [...listaInteracoes]
        .filter((item) => dataEvento(item))
        .sort((a, b) => String(dataEvento(b)).localeCompare(String(dataEvento(a))))[0];

      return {
        lead,
        contratoId: lista?.[0]?.contrato_id || null,
        responsavelId:
          lista?.[0]?.responsavel_id ||
          (leadsComContratoApi.has(String(lead.id)) && !gestor
            ? "__outra_carteira__"
            : getResponsavelId(lead)),
        parcelas: lista,
        pagas,
        abertas,
        encerradas,
        parcelaAtual: atual,
        vencimento,
        dias: diff,
        status,
        cancelamento,
        fechadoEm: dataYmd(lista?.[0]?.fechado_em || lead?.data_fechamento),
        valorTotalContrato: valorTotalContratoSorriso({ parcelas: lista }),
        ultimaInteracao,
      };
    });
  }, [leadsDaCampanha, parcelasPorLead, interacoesPorLead, leadsComContratoApi, gestor]);

  const responsaveisDaCarteira = useMemo(() => {
    const ids = new Set(
      dadosLead.map((item) => item.responsavelId).filter((id) => id !== null && id !== ""),
    );
    return usuarios
      .filter((usuario) => ids.has(usuario?.id) || ids.has(String(usuario?.id)))
      .sort((a, b) => String(a?.nome || "").localeCompare(String(b?.nome || ""), "pt-BR"));
  }, [dadosLead, usuarios]);

  const dadosPorAcesso = useMemo(() => {
    const usuarioId = String(usuarioLogado?.id || "");
    return dadosLead.filter((item) => {
      const responsavelId = String(item.responsavelId || "");
      if (!gestor) return usuarioId && responsavelId === usuarioId;
      if (responsavelFiltro) return responsavelId === String(responsavelFiltro);
      return true;
    });
  }, [dadosLead, gestor, usuarioLogado?.id, responsavelFiltro]);

  const dadosAtivosPorAcesso = useMemo(
    () => dadosPorAcesso.filter((item) => item.status !== "cancelado"),
    [dadosPorAcesso],
  );

  const contagens = useMemo(() => {
    const countAtivo = (status) =>
      dadosAtivosPorAcesso.filter((item) => item.status === status).length;
    return {
      todos: dadosAtivosPorAcesso.length,
      aguardando: countAtivo("aguardando"),
      atrasada: countAtivo("atrasada"),
      hoje: countAtivo("hoje"),
      proximo: countAtivo("proximo") + countAtivo("avencer"),
      // "Pagos" representa pacientes ativos que já possuem ao menos uma baixa.
      pago: dadosAtivosPorAcesso.filter((item) => item.pagas.length > 0).length,
      cancelado: dadosPorAcesso.filter((item) => item.status === "cancelado").length,
    };
  }, [dadosPorAcesso, dadosAtivosPorAcesso]);

  const statsCancelamento = useMemo(() => {
    const cancelados = dadosPorAcesso.filter((item) => item.status === "cancelado");
    const comContrato = dadosPorAcesso.filter((item) => item.parcelas.length > 0);
    const semPagamento = cancelados.filter(
      (item) =>
        String(item.cancelamento?.classificacao || "").toUpperCase() ===
        "SEM_PAGAMENTO",
    ).length;
    const aposInicio = cancelados.filter(
      (item) =>
        String(item.cancelamento?.classificacao || "").toUpperCase() ===
        "APOS_INICIO",
    ).length;

    return {
      total: cancelados.length,
      semPagamento,
      aposInicio,
      taxa:
        comContrato.length > 0 ? (cancelados.length / comContrato.length) * 100 : 0,
    };
  }, [dadosPorAcesso]);

  const dadosVisiveis = useMemo(() => {
    const termo = norm(busca);
    const base =
      situacaoFiltro === "cancelado" ? dadosPorAcesso : dadosAtivosPorAcesso;

    return base
      .filter((item) => {
        if (situacaoFiltro !== "todos") {
          if (situacaoFiltro === "proximo") {
            if (!["proximo", "avencer"].includes(item.status)) return false;
          } else if (situacaoFiltro === "pago") {
            if (!item.pagas.length) return false;
          } else if (item.status !== situacaoFiltro) return false;
        }

        if (dentistaFiltro && String(item.lead?.id_dentista || "") !== String(dentistaFiltro)) {
          return false;
        }

        if (termo) {
          const alvo = norm(
            `${item.lead?.nome_lead || ""} ${item.lead?.telefone1 || ""} ${item.lead?.id || ""}`,
          );
          if (!alvo.includes(termo)) return false;
        }
        return true;
      })
      .sort((a, b) => String(a.lead?.nome_lead || "").localeCompare(String(b.lead?.nome_lead || ""), "pt-BR"));
  }, [dadosPorAcesso, dadosAtivosPorAcesso, busca, situacaoFiltro, dentistaFiltro]);

  const totalPaginas = Math.max(1, Math.ceil(dadosVisiveis.length / itensPorPagina));
  const dadosPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    return dadosVisiveis.slice(inicio, inicio + itensPorPagina);
  }, [dadosVisiveis, paginaAtual, itensPorPagina]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, responsavelFiltro, dentistaFiltro, situacaoFiltro]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [paginaAtual, totalPaginas]);

  useEffect(() => {
    // O painel lateral nasce recolhido e só abre quando o usuário clica
    // em uma linha da tabela. Ao trocar filtros/página, apenas fechamos
    // a seleção se a lead escolhida não estiver mais visível.
    if (!leadSelecionadaId) return;

    const aindaVisivel = dadosPaginados.some(
      (item) => String(item.lead.id) === String(leadSelecionadaId),
    );

    if (!aindaVisivel) setLeadSelecionadaId(null);
  }, [dadosPaginados, leadSelecionadaId]);

  const selecionada = useMemo(
    () =>
      dadosPorAcesso.find(
        (item) => String(item.lead.id) === String(leadSelecionadaId),
      ) || null,
    [dadosPorAcesso, leadSelecionadaId],
  );

  const statsGerais = useMemo(() => {
    const hoje = hojeYmd();
    const itensComContrato = dadosPorAcesso.filter((item) => item.parcelas.length > 0);
    const todasParcelas = itensComContrato.flatMap((item) =>
      item.parcelas.map((parcela) => ({ parcela, item })),
    );

    let totalLancado = 0;
    let carteiraReceber = 0;
    let totalRecebido = 0;
    let totalCancelado = 0;
    let valorAtrasado = 0;
    let valorVencido = 0;

    for (const { parcela, item } of todasParcelas) {
      const valor = Number(parcela?.valor ?? parcela?.valor_boleto ?? 0);
      if (!Number.isFinite(valor)) continue;

      totalLancado += valor;

      const paga = norm(parcela?.status) === "paga" || Boolean(parcela?.receb_lead_id);
      const cancelada =
        norm(parcela?.status) === "cancelada" ||
        (item.status === "cancelado" && !paga);

      const vencimento = dataYmd(parcela?.vencimento || parcela?.data_vencimento);

      if (!cancelada && vencimento && vencimento <= hoje) {
        valorVencido += valor;
      }

      if (paga) {
        totalRecebido += valor;
        continue;
      }

      if (cancelada) {
        totalCancelado += valor;
        continue;
      }

      carteiraReceber += valor;
      if (vencimento && vencimento < hoje) valorAtrasado += valor;
    }

    return {
      pacientesAtivos: dadosAtivosPorAcesso.length,
      contratosLancados: itensComContrato.length,
      quitados: dadosAtivosPorAcesso.filter((item) => item.status === "pago").length,
      totalLancado: Number(totalLancado.toFixed(2)),
      carteiraReceber: Number(carteiraReceber.toFixed(2)),
      totalRecebido: Number(totalRecebido.toFixed(2)),
      totalCancelado: Number(totalCancelado.toFixed(2)),
      valorAtrasado: Number(valorAtrasado.toFixed(2)),
      inadimplencia: valorVencido > 0 ? (valorAtrasado / valorVencido) * 100 : null,
      temParcelas: todasParcelas.length > 0,
    };
  }, [dadosPorAcesso, dadosAtivosPorAcesso]);

  const performanceSorriso = useMemo(() => {
    const hoje = hojeYmd();
    const chaveAtual = hoje.slice(0, 7);
    const diaAtual = Number(hoje.slice(8, 10)) || 1;
    const [anoAtual, mesAtual] = chaveAtual.split("-").map(Number);
    const dataMesAnterior = new Date(anoAtual, mesAtual - 2, 1);
    const chaveAnterior = `${dataMesAnterior.getFullYear()}-${String(dataMesAnterior.getMonth() + 1).padStart(2, "0")}`;
    const chaves = ultimosMeses(8);
    const mapa = new Map(
      chaves.map((key) => [
        key,
        {
          key,
          mes: formatarMesCurto(key),
          vendidos: 0,
          ativados: 0,
          cancelados: 0,
          inadimplentes: 0,
          recebido: 0,
        },
      ]),
    );

    let vendidosTotal = 0;
    let ativadosTotal = 0;
    let ativadosComDataFechamento = 0;
    let canceladosTotal = 0;
    let cancelamentosSemData = 0;
    let inadimplentesAtual = 0;
    let semDataFechamento = 0;
    let contratosTotal = 0;
    const anteriorComparavel = {
      vendidos: 0,
      ativados: 0,
      cancelados: 0,
      recebido: 0,
    };

    const estaNoAnteriorComparavel = (data) => {
      const ymd = dataYmd(data);
      if (!ymd || ymd.slice(0, 7) !== chaveAnterior) return false;
      return (Number(ymd.slice(8, 10)) || 0) <= diaAtual;
    };

    for (const item of dadosPorAcesso) {
      if (item?.parcelas?.length > 0) contratosTotal += 1;

      const canceladoEm = dataYmd(item?.cancelamento?.canceladoEm);
      if (item?.status === "cancelado") {
        canceladosTotal += 1;
        if (canceladoEm) {
          const rowCancelamento = mapa.get(canceladoEm.slice(0, 7));
          if (rowCancelamento) rowCancelamento.cancelados += 1;
          if (estaNoAnteriorComparavel(canceladoEm)) anteriorComparavel.cancelados += 1;
        } else {
          cancelamentosSemData += 1;
        }
      }

      const fechamento = dataYmd(item?.fechadoEm);
      if (fechamento) {
        vendidosTotal += 1;
        const row = mapa.get(fechamento.slice(0, 7));
        if (row) row.vendidos += 1;
        if (estaNoAnteriorComparavel(fechamento)) anteriorComparavel.vendidos += 1;
      } else if (item?.parcelas?.length > 0) {
        semDataFechamento += 1;
      }

      const datasPagamento = (item?.pagas || [])
        .map((p) => dataYmd(p?.data_pagamento || p?.baixado_em))
        .filter(Boolean)
        .sort();

      if (datasPagamento.length > 0) {
        ativadosTotal += 1;
        if (fechamento) ativadosComDataFechamento += 1;
        const primeiraAtivacao = datasPagamento[0];
        const rowAtivacao = mapa.get(primeiraAtivacao.slice(0, 7));
        if (rowAtivacao) rowAtivacao.ativados += 1;
        if (estaNoAnteriorComparavel(primeiraAtivacao)) anteriorComparavel.ativados += 1;
      }

      for (const parcela of item?.pagas || []) {
        const dataPagamento = dataYmd(parcela?.data_pagamento || parcela?.baixado_em);
        if (!dataPagamento) continue;
        const valorPagamento = Number(parcela?.valor ?? parcela?.valor_boleto ?? 0);
        const rowPagamento = mapa.get(dataPagamento.slice(0, 7));
        if (rowPagamento) rowPagamento.recebido += valorPagamento;
        if (estaNoAnteriorComparavel(dataPagamento)) anteriorComparavel.recebido += valorPagamento;
      }

      if (item?.status === "atrasada") {
        inadimplentesAtual += 1;
        const primeiraVencida = (item?.abertas || [])
          .map((p) => dataYmd(p?.vencimento || p?.data_vencimento))
          .filter((vencimento) => vencimento && vencimento < hoje)
          .sort()[0];
        const rowAtraso = primeiraVencida ? mapa.get(primeiraVencida.slice(0, 7)) : null;
        if (rowAtraso) rowAtraso.inadimplentes += 1;
      }
    }

    const meses = Array.from(mapa.values()).map((row) => ({
      ...row,
      recebido: Number(row.recebido.toFixed(2)),
    }));
    const atual = meses[meses.length - 1] || {
      vendidos: 0,
      ativados: 0,
      cancelados: 0,
      inadimplentes: 0,
      recebido: 0,
    };
    const anterior = meses[meses.length - 2] || {
      vendidos: 0,
      ativados: 0,
      cancelados: 0,
      inadimplentes: 0,
      recebido: 0,
    };

    return {
      meses,
      atual,
      anterior,
      anteriorComparavel: {
        ...anteriorComparavel,
        recebido: Number(anteriorComparavel.recebido.toFixed(2)),
      },
      chaveAnterior,
      diaComparacao: diaAtual,
      vendidosTotal,
      ativadosTotal,
      canceladosTotal,
      cancelamentosSemData,
      inadimplentesAtual,
      semDataFechamento,
      taxaAtivacao:
        vendidosTotal > 0 ? (ativadosComDataFechamento / vendidosTotal) * 100 : null,
      taxaCancelamento:
        contratosTotal > 0 ? (canceladosTotal / contratosTotal) * 100 : null,
    };
  }, [dadosPorAcesso]);

  const qualidadePorCaptador = useMemo(() => {
    const hoje = hojeYmd();
    const mapa = new Map();

    const obterCaptadorVendaId = (item) =>
      item?.cancelamento?.captadorId ||
      item?.lead?.usuario_criador_id ||
      item?.lead?.id_usuario ||
      item?.responsavelId ||
      null;

    for (const item of dadosPorAcesso) {
      if (!item?.parcelas?.length) continue;

      const captadorId = obterCaptadorVendaId(item);
      const chave = String(captadorId || "sem-captador");
      const atual = mapa.get(chave) || {
        captadorId,
        nome: captadorId ? nomeUsuario(captadorId, usuarios) : "Sem captador",
        contratos: 0,
        ativos: 0,
        inadimplentes: 0,
        valorAtrasado: 0,
        cancelados: 0,
        canceladosSemPagamento: 0,
        canceladosAposInicio: 0,
      };

      atual.contratos += 1;

      if (item.status === "cancelado") {
        atual.cancelados += 1;
        const classificacao = String(item?.cancelamento?.classificacao || "").toUpperCase();
        if (classificacao === "SEM_PAGAMENTO") atual.canceladosSemPagamento += 1;
        if (classificacao === "APOS_INICIO") atual.canceladosAposInicio += 1;
      } else {
        atual.ativos += 1;

        if (item.status === "atrasada") {
          atual.inadimplentes += 1;
          atual.valorAtrasado += (item?.abertas || []).reduce((soma, parcela) => {
            const vencimento = dataYmd(parcela?.vencimento || parcela?.data_vencimento);
            if (!vencimento || vencimento >= hoje) return soma;
            return soma + Number(parcela?.valor ?? parcela?.valor_boleto ?? 0);
          }, 0);
        }
      }

      mapa.set(chave, atual);
    }

    const todos = Array.from(mapa.values()).map((item) => ({
      ...item,
      valorAtrasado: Number(item.valorAtrasado.toFixed(2)),
      taxaInadimplencia:
        item.ativos > 0 ? (item.inadimplentes / item.ativos) * 100 : 0,
      taxaCancelamento:
        item.contratos > 0 ? (item.cancelados / item.contratos) * 100 : 0,
    }));

    const rankingInadimplencia = [...todos]
      .filter((item) => item.ativos > 0)
      .sort((a, b) =>
        b.taxaInadimplencia - a.taxaInadimplencia ||
        b.inadimplentes - a.inadimplentes ||
        b.valorAtrasado - a.valorAtrasado ||
        String(a.nome).localeCompare(String(b.nome), "pt-BR"),
      )
      .slice(0, 8);

    const rankingCancelamentos = [...todos]
      .filter((item) => item.contratos > 0)
      .sort((a, b) =>
        b.taxaCancelamento - a.taxaCancelamento ||
        b.cancelados - a.cancelados ||
        b.canceladosSemPagamento - a.canceladosSemPagamento ||
        String(a.nome).localeCompare(String(b.nome), "pt-BR"),
      )
      .slice(0, 8);

    return { todos, rankingInadimplencia, rankingCancelamentos };
  }, [dadosPorAcesso, usuarios]);

  const tendenciasPerformance = useMemo(() => ({
    vendidos: tendenciaPercentual(
      performanceSorriso.atual.vendidos,
      performanceSorriso.anteriorComparavel?.vendidos,
    ),
    ativados: tendenciaPercentual(
      performanceSorriso.atual.ativados,
      performanceSorriso.anteriorComparavel?.ativados,
    ),
    cancelados: tendenciaPercentual(
      performanceSorriso.atual.cancelados,
      performanceSorriso.anteriorComparavel?.cancelados,
      true,
    ),
    recebido: tendenciaPercentual(
      performanceSorriso.atual.recebido,
      performanceSorriso.anteriorComparavel?.recebido,
    ),
  }), [performanceSorriso]);

  const performanceSeriesDisponiveis = useMemo(() => ({
    vendidos: performanceSorriso.meses.some((row) => Number(row.vendidos || 0) > 0),
    ativados: performanceSorriso.meses.some((row) => Number(row.ativados || 0) > 0),
    cancelados: performanceSorriso.meses.some((row) => Number(row.cancelados || 0) > 0),
    recebido: performanceSorriso.meses.some((row) => Number(row.recebido || 0) > 0),
    inadimplentes: performanceSorriso.meses.some((row) => Number(row.inadimplentes || 0) > 0),
  }), [performanceSorriso.meses]);

  const performanceViews = useMemo(() => {
    const views = [];
    if (performanceSeriesDisponiveis.vendidos || performanceSeriesDisponiveis.ativados || performanceSeriesDisponiveis.cancelados) {
      views.push({ value: "fluxo", label: "Fluxo" });
    }
    if (performanceSeriesDisponiveis.recebido) {
      views.push({ value: "recebido", label: "Recebimentos" });
    }
    if (performanceSeriesDisponiveis.inadimplentes) {
      views.push({ value: "inadimplencia", label: "Inadimplência" });
    }
    return views;
  }, [performanceSeriesDisponiveis]);

  const visaoPerformanceAtiva = performanceViews.some((view) => view.value === visaoPerformance)
    ? visaoPerformance
    : performanceViews[0]?.value || null;

  useEffect(() => {
    if (visaoPerformanceAtiva && visaoPerformance !== visaoPerformanceAtiva) {
      setVisaoPerformance(visaoPerformanceAtiva);
    }
  }, [visaoPerformance, visaoPerformanceAtiva]);

  const dadosGraficoPerformance = useMemo(() => {
    if (!visaoPerformanceAtiva) return [];

    return performanceSorriso.meses.filter((row) => {
      if (visaoPerformanceAtiva === "fluxo") {
        return Number(row.vendidos || 0) > 0 || Number(row.ativados || 0) > 0 || Number(row.cancelados || 0) > 0;
      }
      if (visaoPerformanceAtiva === "recebido") {
        return Number(row.recebido || 0) > 0;
      }
      return Number(row.inadimplentes || 0) > 0;
    });
  }, [performanceSorriso.meses, visaoPerformanceAtiva]);

  const stats = useMemo(() => {
    const hoje = hojeYmd();
    const todasParcelas = dadosAtivosPorAcesso.flatMap((item) => item.parcelas);
    const parcelasMes = todasParcelas.filter((p) =>
      dataYmd(p?.vencimento || p?.data_vencimento).startsWith(mesReferencia),
    );
    const previstas = parcelasMes.reduce((s, p) => s + Number(p?.valor ?? p?.valor_boleto ?? 0), 0);
    const recebidasMes = parcelasMes.filter(
      (p) => norm(p?.status) === "paga" || p?.receb_lead_id,
    );
    const recebido = recebidasMes.reduce(
      (s, p) => s + Number(p?.valor ?? p?.valor_boleto ?? 0),
      0,
    );
    const atrasadas = todasParcelas.filter((p) => {
      const paga = norm(p?.status) === "paga" || p?.receb_lead_id;
      const venc = dataYmd(p?.vencimento || p?.data_vencimento);
      return !paga && venc && venc < hoje;
    });
    const valorAtrasado = atrasadas.reduce(
      (s, p) => s + Number(p?.valor ?? p?.valor_boleto ?? 0),
      0,
    );
    const vencidasAteHoje = todasParcelas.filter((p) => {
      const venc = dataYmd(p?.vencimento || p?.data_vencimento);
      return venc && venc <= hoje;
    });
    const valorVencido = vencidasAteHoje.reduce(
      (s, p) => s + Number(p?.valor ?? p?.valor_boleto ?? 0),
      0,
    );
    const inadimplencia = valorVencido > 0 ? (valorAtrasado / valorVencido) * 100 : null;
    const proximas3 = todasParcelas.filter((p) => {
      const paga = norm(p?.status) === "paga" || p?.receb_lead_id;
      const venc = dataYmd(p?.vencimento || p?.data_vencimento);
      const d = venc ? diasEntre(hoje, venc) : null;
      return !paga && d !== null && d >= 1 && d <= 3;
    });
    const valorProximas3 = proximas3.reduce(
      (s, p) => s + Number(p?.valor ?? p?.valor_boleto ?? 0),
      0,
    );

    const faixas = [
      { label: "1 – 5 dias", min: 1, max: 5 },
      { label: "6 – 15 dias", min: 6, max: 15 },
      { label: "16 – 30 dias", min: 16, max: 30 },
      { label: "+ 30 dias", min: 31, max: Infinity },
    ].map((faixa) => {
      const itens = atrasadas.filter((p) => {
        const venc = dataYmd(p?.vencimento || p?.data_vencimento);
        const atraso = venc ? -diasEntre(hoje, venc) : 0;
        return atraso >= faixa.min && atraso <= faixa.max;
      });
      return {
        ...faixa,
        qtd: itens.length,
        valor: itens.reduce((s, p) => s + Number(p?.valor ?? p?.valor_boleto ?? 0), 0),
      };
    });

    return {
      previsto: previstas,
      recebido,
      falta: Math.max(0, previstas - recebido),
      valorAtrasado,
      inadimplencia,
      pagosMes: recebidasMes.length,
      proximas3: proximas3.length,
      valorProximas3,
      faixas,
      temParcelas: todasParcelas.length > 0,
    };
  }, [dadosAtivosPorAcesso, mesReferencia]);

  const nomeCarteira = gestor
    ? responsavelFiltro
      ? nomeUsuario(responsavelFiltro, usuarios)
      : "Todas as carteiras"
    : usuarioLogado?.nome || "Minha carteira";

  const idsVisiveis = useMemo(
    () => dadosPaginados.map((item) => String(item.lead.id)),
    [dadosPaginados],
  );

  const todosVisiveisSelecionados =
    idsVisiveis.length > 0 && idsVisiveis.every((id) => idsSelecionados.has(id));

  const alternarSelecionado = (leadId) => {
    const id = String(leadId);
    setIdsSelecionados((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(id)) proximos.delete(id);
      else proximos.add(id);
      return proximos;
    });
  };

  const alternarTodosVisiveis = () => {
    setIdsSelecionados((anteriores) => {
      const proximos = new Set(anteriores);
      const todosMarcados = idsVisiveis.every((id) => proximos.has(id));
      for (const id of idsVisiveis) {
        if (todosMarcados) proximos.delete(id);
        else proximos.add(id);
      }
      return proximos;
    });
  };

  useEffect(() => {
    const idsPermitidos = new Set(dadosPorAcesso.map((item) => String(item.lead.id)));
    setIdsSelecionados((anteriores) => {
      const proximos = new Set([...anteriores].filter((id) => idsPermitidos.has(id)));
      if (proximos.size === anteriores.size) return anteriores;
      return proximos;
    });
  }, [dadosPorAcesso]);

  const imprimirSelecionados = () => {
    const selecionados = dadosPorAcesso.filter((item) =>
      idsSelecionados.has(String(item.lead.id)),
    );

    if (!selecionados.length) return;

    const escapar = (valor) =>
      String(valor ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const statusTexto = {
      aguardando: "Aguardando parcelas",
      atrasada: "Atrasada",
      hoje: "Vence hoje",
      proximo: "Próxima",
      pago: "Paga",
      cancelado: "Cancelado",
      avencer: "A vencer",
    };

    const linhas = selecionados
      .map((item) => {
        const parcela = item.parcelaAtual;
        const numero = parcela?.numero_parcela ?? parcela?.numero ?? null;
        const total =
          parcela?.total_parcelas ??
          parcela?.quantidade_parcelas ??
          (item.parcelas.length || null);
        const valor = parcela?.valor ?? parcela?.valor_boleto;
        const dias =
          item.dias === null
            ? "—"
            : item.status === "atrasada"
              ? `${Math.abs(item.dias)} atraso`
              : String(item.dias);
        const ultimo = item.ultimaInteracao
          ? `${formatarData(dataEvento(item.ultimaInteracao))} - ${textoEvento(item.ultimaInteracao)}`
          : "—";

        return `<tr>
          <td>${escapar(item.lead?.nome_lead || "Sem nome")}</td>
          <td>${escapar(item.lead?.telefone1 || "—")}</td>
          <td>${escapar(nomeUsuario(item.responsavelId, usuarios))}</td>
          <td class="center">${escapar(formatarData(item.fechadoEm))}</td>
          <td class="center">${escapar(numero ? `${numero}/${total || "?"}` : "—")}</td>
          <td class="center">${escapar(formatarData(item.vencimento))}</td>
          <td class="right">${escapar(valor != null ? moeda(valor) : "—")}</td>
          <td>${escapar(statusTexto[item.status] || "A vencer")}</td>
          <td class="center">${escapar(dias)}</td>
          <td>${escapar(ultimo)}</td>
        </tr>`;
      })
      .join("");

    const janela = window.open("", "_blank", "width=1200,height=800");
    if (!janela) return;

    janela.document.write(`<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>Sorriso Planejado - Lista selecionada</title>
<style>
  @page { size: landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #1f2937; font-family: Inter, "Segoe UI", Arial, sans-serif; font-size: 11px; }
  .cabecalho { border-bottom: 2px solid #1d4ed8; padding-bottom: 10px; margin-bottom: 14px; }
  h1 { margin: 0; font-size: 20px; font-weight: 600; color: #0f172a; }
  .sub { margin-top: 4px; color: #64748b; font-size: 11px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f1f5f9; color: #475569; text-align: left; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; padding: 7px 6px; border: 1px solid #cbd5e1; }
  td { padding: 7px 6px; border: 1px solid #e2e8f0; vertical-align: top; }
  tbody tr:nth-child(even) { background: #f8fafc; }
  .center { text-align: center; }
  .right { text-align: right; white-space: nowrap; }
  .rodape { margin-top: 10px; color: #64748b; font-size: 9px; }
</style>
</head>
<body>
  <div class="cabecalho">
    <h1>Sorriso Planejado</h1>
    <div class="sub">${escapar(nomeCarteira)} • ${escapar(formatarMes(mesReferencia))} • ${selecionados.length} paciente(s) selecionado(s)</div>
  </div>
  <table>
    <thead><tr>
      <th>Paciente</th><th>Telefone</th><th>Captador</th><th>Fechamento</th><th>Parcela</th><th>Vencimento</th><th>Valor</th><th>Situação</th><th>Dias</th><th>Último contato</th>
    </tr></thead>
    <tbody>${linhas}</tbody>
  </table>
  <div class="rodape">Impresso em ${escapar(new Date().toLocaleString("pt-BR"))}</div>
</body></html>`);
    janela.document.close();
    janela.focus();
    setTimeout(() => janela.print(), 250);
  };

  const limparFiltros = () => {
    setBusca("");
    setDentistaFiltro("");
    setSituacaoFiltro("todos");
  };

  const tarefasSelecionada = selecionada
    ? tarefasPorLead.get(String(selecionada.lead.id)) || []
    : [];
  const interacoesSelecionada = selecionada
    ? interacoesPorLead.get(String(selecionada.lead.id)) || []
    : [];

  const linhasResumoSelecionada = selecionada
    ? selecionada.status === "cancelado"
      ? [
          ["Campanha", "Sorriso Planejado"],
          ["Dentista", nomeDentista(selecionada.lead?.id_dentista, dentistas)],
          ["Captador da venda", nomeUsuario(selecionada.cancelamento?.captadorId || selecionada.lead?.usuario_criador_id, usuarios)],
          ["Captador da carteira", nomeUsuario(selecionada.responsavelId, usuarios)],
          ["Fechado em", formatarData(selecionada.fechadoEm)],
          ["Situação", <BadgeStatus key="st" status="cancelado" />],
          ["Classificação", rotuloClassificacaoCancelamento(selecionada.cancelamento?.classificacao)],
          ["Cancelado em", formatarDataHora(selecionada.cancelamento?.canceladoEm)],
          ["Cancelado por", nomeUsuario(selecionada.cancelamento?.canceladoPor, usuarios)],
          ["Motivo", rotuloMotivoCancelamento(selecionada.cancelamento?.motivo)],
          ["Parcelas pagas", selecionada.cancelamento?.parcelasPagas ?? selecionada.pagas.length],
          ["Parcelas encerradas", selecionada.cancelamento?.parcelasEncerradas ?? selecionada.encerradas.length],
          ["Pago nas parcelas", moeda(selecionada.cancelamento?.valorPagoParcelas || 0)],
          ["Saldo encerrado", moeda(selecionada.cancelamento?.saldoEncerrado || 0)],
          ["Tempo até cancelar", `${Number(selecionada.cancelamento?.diasAteCancelamento || 0)} dia(s)`],
          ["Descrição", selecionada.cancelamento?.descricao || "—"],
        ]
      : [
          ["Campanha", "Sorriso Planejado"],
          ["Dentista", nomeDentista(selecionada.lead?.id_dentista, dentistas)],
          ["Captador", nomeUsuario(selecionada.responsavelId, usuarios)],
          ["Fechado em", formatarData(selecionada.fechadoEm)],
          ["Situação", <BadgeStatus key="st" status={selecionada.status} />],
          ["Parcela atual", selecionada.parcelaAtual ? `${selecionada.parcelaAtual?.numero_parcela ?? selecionada.parcelaAtual?.numero ?? "—"}/${selecionada.parcelaAtual?.total_parcelas ?? selecionada.parcelas.length}` : "—"],
          ["Próximo vencimento", formatarData(selecionada.vencimento)],
          ["Valor da parcela", selecionada.parcelaAtual ? moeda(selecionada.parcelaAtual?.valor ?? selecionada.parcelaAtual?.valor_boleto) : "—"],
          ["Valor total contrato", Number(selecionada.valorTotalContrato) > 0 ? moeda(selecionada.valorTotalContrato) : "—"],
          ["Parcelas pagas", selecionada.parcelas.length ? selecionada.pagas.length : "—"],
          ["Parcelas em aberto", selecionada.parcelas.length ? selecionada.abertas.length : "—"],
          ["Inadimplência", selecionada.status === "atrasada" && selecionada.dias !== null ? `${Math.abs(selecionada.dias)} dias` : selecionada.parcelas.length ? "Em dia" : "—"],
        ]
    : [];

  return (
    <div
      className="min-h-[calc(100vh-16px)] bg-slate-50 px-2 pb-4 pt-1 text-slate-900"
      style={{ fontFamily: '"Segoe UI Variable", "Segoe UI", Arial, sans-serif', textRendering: "optimizeLegibility" }}
    >
      <div className="mx-auto max-w-[99%]">
        <div className="flex min-h-[54px] items-center justify-between gap-3 border-b border-slate-200 bg-white px-2 py-2">
          <div className="inline-flex h-11 items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 shadow-sm">
            <button
              type="button"
              onClick={onVoltar}
              title="Leads"
              aria-label="Ir para Leads"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-transparent text-slate-500 transition hover:bg-white hover:text-blue-700"
            >
              <Users className="h-[18px] w-[18px] shrink-0" />
            </button>
            <button
              type="button"
              title="Sorriso Planejado"
              aria-label="Sorriso Planejado"
              aria-current="page"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-700 bg-blue-700 text-white shadow-sm transition"
            >
              <HandCoins className="h-[18px] w-[18px] shrink-0" />
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={async () => { await carregarParcelasApi(); await onAtualizar?.(); }}
              disabled={carregando}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[12px] font-medium text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCcw className={`h-4 w-4 shrink-0 ${carregando || carregandoParcelasApi ? "animate-spin" : ""}`} />
              Atualizar
            </button>
            <button
              type="button"
              onClick={onNovaLead}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-700 px-3.5 text-[12px] font-medium text-white shadow-sm transition hover:bg-blue-800"
            >
              <PlusCircle className="h-4 w-4 shrink-0" /> Nova Lead
            </button>
          </div>
        </div>

        {!campanhaSorriso && !carregando && (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">Campanha Sorriso Planejado não encontrada nesta empresa.</div>
                <div className="mt-1">A carteira só considera leads oficialmente vinculadas a essa campanha.</div>
              </div>
            </div>
          </div>
        )}

        <section className="mt-3 rounded-xl border border-slate-200 bg-white p-3 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-700 text-white shadow-sm ring-4 ring-blue-50">
                <HandCoins className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-[18px] font-semibold tracking-tight text-slate-900">Sorriso Planejado</h2>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.02em] text-blue-700">Carteira financeira</span>
                </div>
                <p className="mt-0.5 text-[12px] text-slate-500">Controle de parcelas, cobranças e acompanhamento dos pacientes</p>
                <p className="mt-1 text-[11px] font-medium text-slate-600">
                  {gestor && !responsavelFiltro ? "Visão geral" : "Minha carteira"} · {nomeCarteira}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {gestor && (
                <select
                  value={responsavelFiltro}
                  onChange={(event) => setResponsavelFiltro(event.target.value)}
                  className="h-9 min-w-[190px] rounded-lg border border-slate-300 bg-white px-3 text-[12px] font-medium text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Todas as carteiras</option>
                  {responsaveisDaCarteira.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nome}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="month"
                value={mesReferencia}
                onChange={(event) => setMesReferencia(event.target.value)}
                title={formatarMes(mesReferencia)}
                className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-[12px] font-medium text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>

          <section className="mt-3 overflow-hidden rounded-xl border border-slate-400 bg-white shadow-[0_4px_14px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/80">
            <div className="flex flex-col gap-2 border-b border-slate-300 bg-gradient-to-r from-blue-100 via-white to-violet-100 px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-600 shadow-[0_0_0_3px_rgba(37,99,235,0.12)]" />
                  <div className="text-[12px] font-bold tracking-tight text-slate-900">Performance do Sorriso Planejado</div>
                  {performanceSorriso.semDataFechamento > 0 ? (
                    <span className="rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 shadow-sm">
                      ⚠ {performanceSorriso.semDataFechamento} sem data de fechamento
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 pl-4 text-[10px] text-slate-500">
                  Histórico recente · tendência do mês compara com os mesmos dias do mês anterior
                </div>
              </div>

              {performanceViews.length > 0 ? (
                <div className="inline-flex w-fit rounded-lg border border-slate-300 bg-white p-0.5 shadow-sm">
                  {performanceViews.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setVisaoPerformance(value)}
                      aria-pressed={visaoPerformanceAtiva === value}
                      className={`rounded-md border px-2.5 py-1 text-[10px] font-semibold transition ${
                        visaoPerformanceAtiva === value
                          ? "border-slate-800 bg-slate-900 text-white shadow-sm"
                          : "border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-100 hover:text-slate-800"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-2 border-b border-slate-300 bg-slate-50/70 p-2 sm:grid-cols-5">
              {[
                { label: "Vendidos", valor: performanceSorriso.atual.vendidos, classe: "text-blue-700", dot: "bg-blue-500", view: "fluxo", tendencia: tendenciasPerformance.vendidos },
                { label: "Ativados", valor: performanceSorriso.atual.ativados, classe: "text-emerald-700", dot: "bg-emerald-500", view: "fluxo", tendencia: tendenciasPerformance.ativados },
                { label: "Cancelados", valor: performanceSorriso.atual.cancelados, classe: "text-rose-700", dot: "bg-rose-500", view: "fluxo", tendencia: tendenciasPerformance.cancelados },
                {
                  label: "Inadimplentes",
                  valor: performanceSorriso.inadimplentesAtual,
                  classe: "text-amber-700",
                  dot: "bg-amber-500",
                  view: "inadimplencia",
                  detalhe: statsGerais.inadimplencia !== null
                    ? `${statsGerais.inadimplencia.toFixed(1).replace(".", ",")}% do valor vencido`
                    : "Sem base vencida",
                },
                { label: "Recebido", valor: moeda(performanceSorriso.atual.recebido), classe: "text-violet-700", dot: "bg-violet-500", view: "recebido", tendencia: tendenciasPerformance.recebido },
              ].map(({ label, valor, classe, dot, view, tendencia, detalhe }) => {
                const disponivel = performanceViews.some((item) => item.value === view);
                const ativo = visaoPerformanceAtiva === view;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => disponivel && setVisaoPerformance(view)}
                    className={`min-w-0 rounded-lg border bg-white px-3 py-2 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition ${
                      ativo
                        ? "border-blue-400 ring-2 ring-blue-100"
                        : "border-slate-300 hover:border-blue-300 hover:bg-blue-50/30"
                    } ${disponivel ? "cursor-pointer" : "cursor-default"}`}
                    title={disponivel ? `Abrir visão: ${view === "fluxo" ? "Fluxo" : view === "recebido" ? "Recebimentos" : "Inadimplência"}` : undefined}
                  >
                    <div className="flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.02em] text-slate-500">
                      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                      {label}
                    </div>
                    <div className={`mt-0.5 truncate text-[15px] font-extrabold tracking-tight ${classe}`}>{valor}</div>
                    {tendencia ? (
                      <div className={`mt-1 inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[8px] font-bold ${tendencia.fundo} ${tendencia.classe}`}>
                        <span className="text-[11px] leading-none">{tendencia.simbolo}</span>
                        <span>{tendencia.texto}</span>
                        <span className="truncate font-medium text-slate-400">vs mês ant.</span>
                      </div>
                    ) : (
                      <div className="mt-1 truncate text-[8px] font-semibold text-amber-700">{detalhe}</div>
                    )}
                  </button>
                );
              })}
            </div>

            {visaoPerformanceAtiva && dadosGraficoPerformance.length > 0 ? (
              <div className="px-3 pb-3 pt-2.5">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-[11px] font-bold text-slate-800">
                      {visaoPerformanceAtiva === "fluxo"
                        ? "Movimento comercial por mês"
                        : visaoPerformanceAtiva === "recebido"
                          ? "Recebimentos por data da baixa"
                          : "Inadimplentes atuais por mês do primeiro atraso"}
                    </div>
                    <div className="mt-0.5 text-[8px] text-slate-400">
                      {visaoPerformanceAtiva === "fluxo"
                        ? "Venda = fechamento · Ativação = primeiro pagamento · Cancelamento = data real do cancelamento"
                        : visaoPerformanceAtiva === "recebido"
                          ? "Somente meses que tiveram entrada financeira aparecem abaixo."
                          : "Não é fotografia histórica: mostra os inadimplentes de hoje pelo mês em que o atraso começou."}
                    </div>
                  </div>

                  {visaoPerformanceAtiva === "fluxo" ? (
                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold text-slate-600">
                      {performanceSeriesDisponiveis.vendidos ? <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Vendidos</span> : null}
                      {performanceSeriesDisponiveis.ativados ? <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Ativados</span> : null}
                      {performanceSeriesDisponiveis.cancelados ? <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2 py-1"><span className="h-2 w-2 rounded-full bg-rose-500" /> Cancelados</span> : null}
                    </div>
                  ) : visaoPerformanceAtiva === "recebido" ? (
                    <div className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">
                      Mês atual: {moeda(performanceSorriso.atual.recebido)}
                    </div>
                  ) : (
                    <div className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700">
                      Hoje: {performanceSorriso.inadimplentesAtual} paciente(s)
                    </div>
                  )}
                </div>

                <div className="h-[126px] rounded-lg border border-slate-300 bg-gradient-to-b from-white to-slate-50/80 px-1 py-1 shadow-inner">
                  <ResponsiveContainer width="100%" height="100%">
                    {visaoPerformanceAtiva === "fluxo" ? (
                      <LineChart data={dadosGraficoPerformance} margin={{ top: 8, right: 10, left: -22, bottom: 0 }}>
                        <CartesianGrid stroke="#cbd5e1" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fill: "#475569", fontSize: 8, fontWeight: 600 }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 8 }} axisLine={false} tickLine={false} width={30} />
                        <Tooltip
                          formatter={(value, name) => [Number(value || 0).toLocaleString("pt-BR"), name]}
                          labelStyle={{ color: "#0f172a", fontSize: 10, fontWeight: 700 }}
                          contentStyle={{ fontSize: 9, borderRadius: 8, border: "1px solid #94a3b8", padding: "6px 8px", boxShadow: "0 8px 20px rgba(15,23,42,.12)" }}
                        />
                        {performanceSeriesDisponiveis.vendidos ? <Line type="monotone" dataKey="vendidos" name="Vendidos" stroke="#2563eb" strokeWidth={2.6} dot={{ r: 3, strokeWidth: 2, fill: "#fff" }} activeDot={{ r: 5 }} isAnimationActive={false} /> : null}
                        {performanceSeriesDisponiveis.ativados ? <Line type="monotone" dataKey="ativados" name="Ativados" stroke="#059669" strokeWidth={2.6} dot={{ r: 3, strokeWidth: 2, fill: "#fff" }} activeDot={{ r: 5 }} isAnimationActive={false} /> : null}
                        {performanceSeriesDisponiveis.cancelados ? <Line type="monotone" dataKey="cancelados" name="Cancelados" stroke="#e11d48" strokeWidth={2.4} dot={{ r: 3, strokeWidth: 2, fill: "#fff" }} activeDot={{ r: 5 }} isAnimationActive={false} /> : null}
                      </LineChart>
                    ) : visaoPerformanceAtiva === "recebido" ? (
                      <BarChart data={dadosGraficoPerformance} margin={{ top: 8, right: 10, left: -12, bottom: 0 }}>
                        <CartesianGrid stroke="#cbd5e1" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fill: "#475569", fontSize: 8, fontWeight: 600 }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
                        <YAxis
                          tick={{ fill: "#64748b", fontSize: 8 }}
                          axisLine={false}
                          tickLine={false}
                          width={42}
                          tickFormatter={(value) => Number(value || 0).toLocaleString("pt-BR", { notation: "compact", maximumFractionDigits: 1 })}
                        />
                        <Tooltip
                          formatter={(value) => [moeda(value), "Recebido"]}
                          labelStyle={{ color: "#0f172a", fontSize: 10, fontWeight: 700 }}
                          contentStyle={{ fontSize: 9, borderRadius: 8, border: "1px solid #94a3b8", padding: "6px 8px", boxShadow: "0 8px 20px rgba(15,23,42,.12)" }}
                        />
                        <Bar dataKey="recebido" name="Recebido" fill="#7c3aed" radius={[5, 5, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    ) : (
                      <BarChart data={dadosGraficoPerformance} margin={{ top: 8, right: 10, left: -22, bottom: 0 }}>
                        <CartesianGrid stroke="#cbd5e1" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fill: "#475569", fontSize: 8, fontWeight: 600 }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 8 }} axisLine={false} tickLine={false} width={30} />
                        <Tooltip
                          formatter={(value) => [Number(value || 0).toLocaleString("pt-BR"), "Inadimplentes"]}
                          labelStyle={{ color: "#0f172a", fontSize: 10, fontWeight: 700 }}
                          contentStyle={{ fontSize: 9, borderRadius: 8, border: "1px solid #94a3b8", padding: "6px 8px", boxShadow: "0 8px 20px rgba(15,23,42,.12)" }}
                        />
                        <Bar dataKey="inadimplentes" name="Inadimplentes" fill="#f59e0b" radius={[5, 5, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>

                {visaoPerformanceAtiva === "inadimplencia" &&
                (qualidadePorCaptador.rankingInadimplencia.length > 0 ||
                  qualidadePorCaptador.rankingCancelamentos.length > 0) ? (
                  <div className="mt-2 grid gap-2 lg:grid-cols-2">
                    <section className="overflow-hidden rounded-lg border border-amber-300 bg-white shadow-sm">
                      <div className="border-b border-amber-200 bg-gradient-to-r from-amber-100 via-amber-50 to-white px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-[11px] font-bold text-slate-900">Inadimplência por captador</div>
                            <div className="mt-0.5 text-[9px] text-slate-500">Pacientes atrasados ÷ contratos ativos do captador</div>
                          </div>
                          <span className="rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[9px] font-bold text-amber-800">Hoje</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {qualidadePorCaptador.rankingInadimplencia.map((item, index) => (
                          <div key={`inad-${item.captadorId || item.nome}`} className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-[9px] font-bold text-amber-800">
                                {index + 1}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate text-[10px] font-semibold text-slate-800" title={item.nome}>{item.nome}</span>
                                  <span className="shrink-0 text-[10px] font-bold text-amber-800">
                                    {item.taxaInadimplencia.toFixed(1).replace(".", ",")}%
                                  </span>
                                </div>
                                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-amber-100">
                                  <div
                                    className="h-full rounded-full bg-amber-500"
                                    style={{ width: `${Math.max(3, Math.min(100, item.taxaInadimplencia))}%` }}
                                  />
                                </div>
                                <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-[9px] text-slate-500">
                                  <span><strong className="text-slate-700">{item.inadimplentes}</strong> inadimplente(s) em {item.ativos} ativo(s)</span>
                                  <span className="font-semibold text-amber-700">{moeda(item.valorAtrasado)} em atraso</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="overflow-hidden rounded-lg border border-rose-300 bg-white shadow-sm">
                      <div className="border-b border-rose-200 bg-gradient-to-r from-rose-100 via-rose-50 to-white px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-[11px] font-bold text-slate-900">Cancelamentos por captador</div>
                            <div className="mt-0.5 text-[9px] text-slate-500">Contratos cancelados ÷ contratos vendidos pelo captador</div>
                          </div>
                          <span className="rounded-full border border-rose-300 bg-white px-2 py-0.5 text-[9px] font-bold text-rose-700">Qualidade da venda</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {qualidadePorCaptador.rankingCancelamentos.map((item, index) => (
                          <div key={`cancel-${item.captadorId || item.nome}`} className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-[9px] font-bold text-rose-700">
                                {index + 1}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="truncate text-[10px] font-semibold text-slate-800" title={item.nome}>{item.nome}</span>
                                  <span className="shrink-0 text-[10px] font-bold text-rose-700">
                                    {item.taxaCancelamento.toFixed(1).replace(".", ",")}%
                                  </span>
                                </div>
                                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-rose-100">
                                  <div
                                    className="h-full rounded-full bg-rose-500"
                                    style={{ width: `${Math.max(3, Math.min(100, item.taxaCancelamento))}%` }}
                                  />
                                </div>
                                <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-[9px] text-slate-500">
                                  <span><strong className="text-slate-700">{item.cancelados}</strong> cancelado(s) em {item.contratos} contrato(s)</span>
                                  <span className="font-semibold text-rose-700">
                                    {item.canceladosSemPagamento} sem 1º pagamento · {item.canceladosAposInicio} após início
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[9px] text-slate-500">
                      Leitura para treinamento: compare <strong className="text-slate-700">percentual e volume</strong>. Quem vende mais pode ter mais ocorrências em quantidade; a taxa mostra a qualidade proporcional da carteira vendida.
                    </div>
                  </div>
                ) : null}

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-2 text-[8px] text-slate-500">
                  <span>
                    Ativação: <strong className="text-emerald-700">{performanceSorriso.taxaAtivacao !== null ? `${performanceSorriso.taxaAtivacao.toFixed(1).replace(".", ",")}%` : "—"}</strong>
                    <span className="mx-1.5">·</span>
                    Cancelamento: <strong className="text-rose-700">{performanceSorriso.taxaCancelamento !== null ? `${performanceSorriso.taxaCancelamento.toFixed(1).replace(".", ",")}%` : "—"}</strong>
                    <span className="mx-1.5">·</span>
                    Inadimplência: <strong className="text-amber-700">{statsGerais.inadimplencia !== null ? `${statsGerais.inadimplencia.toFixed(1).replace(".", ",")}%` : "—"}</strong>
                  </span>
                  <span>{performanceSorriso.vendidosTotal} contrato(s) com data de fechamento</span>
                </div>
              </div>
            ) : null}
          </section>

          {/* Resumo compacto: preserva todos os indicadores sem ocupar a tela com vários cards. */}
          <div className="mt-3 grid gap-2 xl:grid-cols-[minmax(0,1.65fr)_minmax(390px,0.95fr)]">
            <section className="overflow-hidden rounded-xl border border-indigo-300 bg-white shadow-[0_2px_6px_rgba(15,23,42,0.07)]">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-200 bg-gradient-to-r from-blue-200 via-indigo-100 to-violet-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-blue-200 bg-white/80 text-blue-700 shadow-sm">
                    <CircleDollarSign className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[12px] font-semibold text-slate-900">Carteira geral</div>
                    <div className="text-[10px] text-slate-600">Acumulado desde o início · independente do mês</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                  <span><strong className="text-slate-800">{statsGerais.contratosLancados}</strong> contratos</span>
                  <span><strong className="text-blue-700">{statsGerais.pacientesAtivos}</strong> ativos</span>
                  <span><strong className="text-emerald-700">{statsGerais.quitados}</strong> quitados</span>
                </div>
              </div>

              <div className="grid grid-cols-2 divide-x divide-y divide-indigo-100 md:grid-cols-4 md:divide-y-0">
                {[
                  {
                    label: "Total lançado",
                    valor: statsGerais.temParcelas ? moeda(statsGerais.totalLancado) : "—",
                    detalhe: "Tudo que virou parcela",
                    classe: "text-violet-700",
                  },
                  {
                    label: "Carteira a receber",
                    valor: statsGerais.temParcelas ? moeda(statsGerais.carteiraReceber) : "—",
                    detalhe: "Em aberto nos ativos",
                    classe: "text-blue-700",
                  },
                  {
                    label: "Já recebido",
                    valor: statsGerais.temParcelas ? moeda(statsGerais.totalRecebido) : "—",
                    detalhe: "Desde o início",
                    classe: "text-emerald-700",
                  },
                  {
                    label: "Em atraso",
                    valor: statsGerais.temParcelas ? moeda(statsGerais.valorAtrasado) : "—",
                    detalhe: `${contagens.atrasada} paciente(s)`,
                    classe: "text-rose-700",
                  },
                ].map((item, index) => (
                  <div
                    key={item.label}
                    className={`min-w-0 px-3 py-2.5 ${[
                      "bg-violet-50/70",
                      "bg-blue-50/70",
                      "bg-emerald-50/70",
                      "bg-rose-50/70",
                    ][index]}`}
                  >
                    <div className="truncate text-[10px] font-semibold uppercase tracking-[0.02em] text-slate-400">{item.label}</div>
                    <div className={`mt-1 truncate text-[17px] font-semibold tracking-tight ${item.classe}`}>{item.valor}</div>
                    <div className="mt-0.5 truncate text-[10px] text-slate-400">{item.detalhe}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-indigo-100 bg-gradient-to-r from-slate-50 via-blue-50/70 to-violet-50/70 px-3 py-1.5 text-[10px] text-slate-600">
                <span>
                  Cancelado:
                  <strong className="ml-1 text-slate-700">
                    {statsGerais.temParcelas ? moeda(statsGerais.totalCancelado) : "—"}
                  </strong>
                </span>
                <span>
                  Inadimplência:
                  <strong className="ml-1 text-rose-700">
                    {statsGerais.inadimplencia !== null
                      ? `${statsGerais.inadimplencia.toFixed(2).replace(".", ",")}%`
                      : "—"}
                  </strong>
                </span>
                <span>
                  Cancelamentos:
                  <strong className="ml-1 text-slate-700">{statsCancelamento.total}</strong>
                </span>
                <span>
                  Sem pagamento:
                  <strong className="ml-1 text-rose-700">{statsCancelamento.semPagamento}</strong>
                </span>
                <span>
                  Após início:
                  <strong className="ml-1 text-amber-700">{statsCancelamento.aposInicio}</strong>
                </span>
                <span>
                  Taxa:
                  <strong className="ml-1 text-violet-700">
                    {statsCancelamento.taxa.toFixed(1).replace(".", ",")}%
                  </strong>
                </span>
              </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-cyan-300 bg-white shadow-[0_2px_6px_rgba(15,23,42,0.07)]">
              <div className="flex items-center justify-between border-b border-cyan-200 bg-gradient-to-r from-emerald-100 via-teal-50 to-cyan-100 px-3 py-2">
                <div>
                  <div className="text-[12px] font-semibold text-slate-900">Movimento do mês</div>
                  <div className="text-[10px] text-slate-600">{formatarMes(mesReferencia)}</div>
                </div>
                <CalendarDays className="h-4 w-4 text-blue-600" />
              </div>

              <div className="grid grid-cols-2">
                {[
                  ["Previsto", stats.temParcelas ? moeda(stats.previsto) : "—", "text-slate-800"],
                  ["Recebido", stats.temParcelas ? moeda(stats.recebido) : "—", "text-emerald-700"],
                  ["Falta", stats.temParcelas ? moeda(stats.falta) : "—", "text-amber-700"],
                  ["Pagas", stats.temParcelas ? `${stats.pagosMes} parcela(s)` : "—", "text-blue-700"],
                ].map(([label, valor, classe], index) => (
                  <div
                    key={label}
                    className={`px-3 py-2.5 ${
                      [
                        "bg-blue-50/60",
                        "bg-emerald-50/70",
                        "bg-amber-50/70",
                        "bg-indigo-50/60",
                      ][index]
                    } ${index % 2 === 0 ? "border-r" : ""} ${index < 2 ? "border-b" : ""} border-cyan-100`}
                  >
                    <div className="text-[10px] font-semibold uppercase tracking-[0.02em] text-slate-400">{label}</div>
                    <div className={`mt-1 truncate text-[14px] font-semibold ${classe}`}>{valor}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="mt-2 overflow-hidden rounded-xl border border-slate-300 bg-slate-50 shadow-[0_2px_6px_rgba(15,23,42,0.06)]">
            <div className="grid grid-cols-1 divide-y divide-slate-100 lg:grid-cols-[1fr_1fr_2.1fr] lg:divide-x lg:divide-y-0">
              <button
                type="button"
                onClick={() => setSituacaoFiltro("aguardando")}
                className="flex min-h-[54px] items-center justify-between gap-3 bg-gradient-to-r from-amber-100/80 to-orange-50 px-3 py-2 text-left transition hover:from-amber-100 hover:to-orange-100/70"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.02em] text-slate-400">Aguardando parcelas</div>
                    <div className="text-sm font-semibold text-slate-800">{contagens.aguardando} pacientes</div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </button>

              <div className="flex min-h-[54px] items-center gap-2 bg-gradient-to-r from-blue-100/70 to-indigo-50 px-3 py-2">
                <CalendarDays className="h-4 w-4 shrink-0 text-blue-600" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.02em] text-slate-400">Próximos 3 dias</div>
                  <div className="text-sm font-semibold text-slate-800">
                    {stats.temParcelas ? `${stats.proximas3} parc. · ${moeda(stats.valorProximas3)}` : "—"}
                  </div>
                </div>
              </div>

              <div className="flex min-h-[54px] flex-wrap items-center gap-x-3 gap-y-1 bg-gradient-to-r from-rose-50 via-pink-50/70 to-orange-50 px-3 py-2">
                <div className="mr-1 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 shrink-0 text-rose-600" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.02em] text-slate-400">Atraso</span>
                </div>
                {stats.faixas.map((faixa) => (
                  <span key={faixa.label} className="whitespace-nowrap text-[10px] text-slate-500">
                    {faixa.label}: <strong className="text-slate-700">{stats.temParcelas ? faixa.qtd : "—"}</strong>
                    {stats.temParcelas ? <span className="ml-1 text-rose-600">{moeda(faixa.valor)}</span> : null}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <div className="mt-3 flex flex-wrap gap-1.5 rounded-xl border border-slate-300 bg-gradient-to-r from-slate-50 via-white to-blue-50/40 p-2 shadow-[0_2px_6px_rgba(15,23,42,0.06)]">
            {[
              ["todos", `Todos (${contagens.todos})`],
              ["atrasada", `Atrasados${stats.temParcelas ? ` (${contagens.atrasada})` : ""}`],
              ["hoje", `Vencem hoje${stats.temParcelas ? ` (${contagens.hoje})` : ""}`],
              ["proximo", `Próximos${stats.temParcelas ? ` (${contagens.proximo})` : ""}`],
              ["pago", `Pagos${stats.temParcelas ? ` (${contagens.pago})` : ""}`],
              ["cancelado", `Cancelados (${contagens.cancelado})`],
              ["aguardando", `Aguardando parcelas (${contagens.aguardando})`],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setSituacaoFiltro(value)}
                className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium transition ${
                  situacaoFiltro === value
                    ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                    : "border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1fr)_150px_150px_160px_84px_170px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Buscar paciente..."
                className="h-9 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-xs outline-none focus:border-blue-500"
              />
            </div>
            <select
              value={situacaoFiltro}
              onChange={(event) => setSituacaoFiltro(event.target.value)}
              className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700"
            >
              <option value="todos">Situação: todas</option>
              <option value="aguardando">Aguardando parcelas</option>
              <option value="atrasada">Atrasada</option>
              <option value="hoje">Vence hoje</option>
              <option value="proximo">Próximas</option>
              <option value="pago">Com pagamento</option>
              <option value="cancelado">Cancelados</option>
            </select>
            <select
              value={dentistaFiltro}
              onChange={(event) => setDentistaFiltro(event.target.value)}
              className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700"
            >
              <option value="">Dentista: todos</option>
              {dentistas.map((dentista) => (
                <option key={dentista.id} value={dentista.id}>
                  {dentista.profissional || dentista.nome}
                </option>
              ))}
            </select>
            <div className="flex h-9 items-center rounded-lg border border-slate-300 bg-slate-50 px-3 text-xs font-semibold text-slate-600">
              Campanha: Sorriso Planejado
            </div>
            <button
              type="button"
              onClick={limparFiltros}
              className="h-9 rounded-lg border border-slate-300 bg-slate-50 px-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={imprimirSelecionados}
              disabled={idsSelecionados.size === 0}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              title={idsSelecionados.size ? "Imprimir somente os pacientes selecionados" : "Selecione pacientes para imprimir"}
            >
              <Printer className="h-4 w-4" />
              Imprimir selecionados ({idsSelecionados.size})
            </button>
          </div>

          <div className={`mt-3 grid items-start gap-3 ${selecionada ? "xl:grid-cols-[minmax(0,1fr)_360px]" : "grid-cols-1"}`}>
            <div className="min-w-0 overflow-x-auto rounded-xl border border-blue-300 bg-white shadow-[0_3px_9px_rgba(15,23,42,0.08)] ring-1 ring-blue-100">
              <table className="w-full min-w-[1160px] border-collapse">
                <thead className="sticky top-0 z-10 border-b border-blue-200 shadow-[0_3px_10px_rgba(37,99,235,0.10)]">
                  <tr className="bg-gradient-to-r from-blue-200 via-indigo-100 to-violet-200 text-left text-[11px] font-semibold uppercase tracking-[0.025em] text-slate-800">
                    <th className="w-8 border-r border-blue-200/70 px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={todosVisiveisSelecionados}
                        onChange={alternarTodosVisiveis}
                        className="h-3.5 w-3.5 cursor-pointer accent-blue-500"
                        title="Selecionar/desmarcar pacientes desta página"
                        aria-label="Selecionar todos os pacientes desta página"
                      />
                    </th>
                    <th className="border-r border-blue-200/70 px-3 py-3">Paciente</th>
                    <th className="w-[160px] border-r border-blue-200/70 px-3 py-3">Captador</th>
                    <th className="border-r border-blue-200/70 px-2 py-3 text-center">Fechamento</th>
                    <th className="border-r border-blue-200/70 px-2 py-3 text-center">Parcela</th>
                    <th className="border-r border-blue-200/70 px-2 py-3 text-center">Vencimento</th>
                    <th className="border-r border-blue-200/70 px-2 py-3 text-right">Valor</th>
                    <th className="border-r border-blue-200/70 px-2 py-3 text-center">Situação</th>
                    <th className="border-r border-blue-200/70 px-2 py-3 text-center">Dias</th>
                    <th className="border-r border-blue-200/70 px-3 py-3">Último contato</th>
                    <th className="w-[194px] min-w-[194px] max-w-[194px] px-2 py-3 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {carregando || carregandoParcelasApi ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-12 text-center text-sm font-semibold text-slate-500">Carregando carteira...</td>
                    </tr>
                  ) : dadosVisiveis.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-500">Nenhum paciente encontrado nesta carteira.</td>
                    </tr>
                  ) : (
                    dadosPaginados.map((item) => {
                      const { lead, parcelaAtual, status, dias, ultimaInteracao, parcelas: lista } = item;
                      const numero = parcelaAtual?.numero_parcela ?? parcelaAtual?.numero ?? null;
                      const total = parcelaAtual?.total_parcelas ?? parcelaAtual?.quantidade_parcelas ?? (lista.length || null);
                      const valor =
                        status === "cancelado"
                          ? item.cancelamento?.saldoEncerrado
                          : parcelaAtual?.valor ?? parcelaAtual?.valor_boleto;
                      const painelAtivo = String(lead.id) === String(leadSelecionadaId);
                      const marcado = idsSelecionados.has(String(lead.id));
                      return (
                        <tr
                          key={lead.id}
                          onClick={() => {
                            const mesmaLead = String(leadSelecionadaId) === String(lead.id);
                            setLeadSelecionadaId(mesmaLead ? null : lead.id);
                            if (!mesmaLead) setAbaDetalhe("resumo");
                          }}
                          className={`cursor-pointer border-b border-slate-100 transition-colors ${
                            status === "cancelado"
                              ? painelAtivo
                                ? "bg-slate-100 shadow-[inset_3px_0_0_#64748b]"
                                : "bg-slate-50 hover:bg-slate-100"
                              : painelAtivo
                                ? "bg-blue-50/70 shadow-[inset_3px_0_0_#2563eb]"
                                : "bg-white hover:bg-blue-50/40"
                          }`}
                        >
                          <td className="px-2 py-2.5">
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={() => alternarSelecionado(lead.id)}
                              onClick={(event) => event.stopPropagation()}
                              className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                              aria-label={`Selecionar ${lead?.nome_lead || "paciente"}`}
                            />
                          </td>
                          <td className="px-2 py-2.5">
                            <div className="text-[12px] font-medium tracking-normal text-slate-800">{lead?.nome_lead || "Sem nome"}</div>
                            <div className="mt-0.5 text-[10px] text-slate-400">{lead?.telefone1 || "Sem telefone"}</div>
                          </td>
                          <td className="w-[160px] px-3 py-2.5">
                            <span
                              className="inline-flex max-w-[150px] items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-700"
                              title={nomeUsuario(item.responsavelId, usuarios)}
                            >
                              <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                              <span className="truncate">{nomeUsuario(item.responsavelId, usuarios)}</span>
                            </span>
                          </td>
                          <td className="px-2 py-2 text-center text-[11px] font-medium text-slate-600">
                            {item.fechadoEm ? (
                              formatarData(item.fechadoEm)
                            ) : (
                              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[8px] font-semibold text-amber-700">
                                Sem data
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center text-[12px] text-slate-600">
                            {status === "cancelado"
                              ? `${item.pagas.length}/${lista.length} pagas`
                              : numero
                                ? `${numero}/${total || "?"}`
                                : "—"}
                          </td>
                          <td className="px-2 py-2 text-center text-[12px] text-slate-600">
                            {status === "cancelado"
                              ? formatarData(item.cancelamento?.canceladoEm)
                              : formatarData(item.vencimento)}
                          </td>
                          <td className="px-2 py-2 text-right text-[12px] font-semibold text-slate-700">
                            {valor != null ? moeda(valor) : "—"}
                          </td>
                          <td className="px-2 py-2 text-center"><BadgeStatus status={status} /></td>
                          <td className={`px-2 py-2 text-center text-[12px] font-medium ${status === "atrasada" ? "text-rose-600" : "text-slate-500"}`}>
                            {dias === null ? "—" : status === "atrasada" ? Math.abs(dias) : dias}
                          </td>
                          <td className="px-2 py-2 text-[11px] text-slate-600">
                            {ultimaInteracao ? (
                              <>
                                <div>{formatarData(dataEvento(ultimaInteracao))}</div>
                                <div className="max-w-[120px] truncate text-[10px] text-slate-400">{textoEvento(ultimaInteracao)}</div>
                              </>
                            ) : "—"}
                          </td>
                          <td className="w-[194px] min-w-[194px] max-w-[194px] px-2 py-2.5" onClick={(event) => event.stopPropagation()}>
                            <div className="mx-auto grid w-[184px] grid-cols-[32px_32px_32px_72px] items-center justify-center gap-1.5 whitespace-nowrap">
                              <button type="button" onClick={() => onWhatsApp?.(lead)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-500 bg-emerald-500 text-white shadow-sm transition hover:bg-emerald-600" title="WhatsApp">
                                <MessageCircle className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={() => onNovaTarefa?.(lead, contextoCobrancaSorriso(lead, parcelaAtual))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition hover:border-blue-300 hover:bg-blue-100" title="Tarefas">
                                <ListTodo className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={() => onInteracoes?.(lead, contextoCobrancaSorriso(lead, parcelaAtual))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700 transition hover:border-amber-300 hover:bg-amber-100" title="Interações">
                                <PhoneCall className="h-3.5 w-3.5" />
                              </button>
                              {status === "aguardando" ? (
                                <button type="button" onClick={() => abrirLancamentoParcelas(lead)} className="inline-flex h-8 w-[72px] items-center justify-center gap-1 rounded-lg border border-violet-600 bg-violet-600 px-2 text-[10px] font-medium text-white shadow-sm transition hover:bg-violet-700" title="Lançar parcelas">
                                  <HandCoins className="h-3 w-3" /> Lançar
                                </button>
                              ) : (
                                <span aria-hidden="true" className="block h-8 w-[72px]" />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              <div className="flex flex-col gap-2 border-t border-blue-100 bg-slate-50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[11px] text-slate-500">
                  {dadosVisiveis.length > 0 ? (
                    <>
                      Mostrando <strong className="text-slate-700">{(paginaAtual - 1) * itensPorPagina + 1}</strong> a{" "}
                      <strong className="text-slate-700">{Math.min(paginaAtual * itensPorPagina, dadosVisiveis.length)}</strong> de{" "}
                      <strong className="text-slate-700">{dadosVisiveis.length}</strong> paciente(s)
                    </>
                  ) : (
                    "Nenhum paciente"
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    Linhas
                    <select
                      value={itensPorPagina}
                      onChange={(event) => {
                        setItensPorPagina(Number(event.target.value));
                        setPaginaAtual(1);
                      }}
                      className="h-7 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-700"
                    >
                      {[10, 20, 30, 50].map((quantidade) => (
                        <option key={quantidade} value={quantidade}>{quantidade}</option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={() => setPaginaAtual((pagina) => Math.max(1, pagina - 1))}
                    disabled={paginaAtual <= 1}
                    className="h-7 rounded-md border border-slate-300 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Anterior
                  </button>
                  <span className="min-w-[74px] text-center text-[11px] font-semibold text-slate-600">
                    {paginaAtual} / {totalPaginas}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPaginaAtual((pagina) => Math.min(totalPaginas, pagina + 1))}
                    disabled={paginaAtual >= totalPaginas}
                    className="h-7 rounded-md border border-slate-300 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </div>

            {selecionada && (
              <aside className="overflow-hidden rounded-xl border border-blue-100 bg-white shadow-[0_6px_20px_rgba(37,99,235,0.08)] xl:sticky xl:top-3">
                <div className="relative overflow-hidden border-b border-indigo-200 bg-gradient-to-r from-blue-200 via-indigo-100 to-violet-200 p-3.5">
                  <span className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-medium tracking-normal text-slate-900">{selecionada.lead?.nome_lead || "Paciente"}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="text-[11px] text-blue-700">☎ {selecionada.lead?.telefone1 || "—"}</span>
                        <button type="button" onClick={() => onWhatsApp?.(selecionada.lead)} className="inline-flex h-7 items-center gap-1 rounded-lg bg-emerald-500 px-2.5 text-[10px] font-medium text-white shadow-sm hover:bg-emerald-600">
                          <MessageCircle className="h-3 w-3" /> Conversar no WhatsApp
                        </button>
                      </div>
                    </div>
                    <button type="button" onClick={() => setLeadSelecionadaId(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Fechar painel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex border-b border-slate-200 px-2 text-[10px] font-medium text-slate-500">
                  {[
                    ["resumo", "Resumo"],
                    ["parcelas", `Parcelas${selecionada.parcelas.length ? ` (${selecionada.parcelas.length})` : ""}`],
                    ["tarefas", `Tarefas (${tarefasSelecionada.length})`],
                    ["interacoes", `Interações (${interacoesSelecionada.length})`],
                  ].map(([aba, label]) => (
                    <button key={aba} type="button" onClick={() => setAbaDetalhe(aba)} className={`border-b-2 px-2.5 py-2.5 transition ${abaDetalhe === aba ? "border-blue-600 text-blue-700" : "border-transparent hover:text-slate-700"}`}>
                      {label}
                    </button>
                  ))}
                </div>

                <div className="max-h-[430px] overflow-y-auto p-3.5">
                  {abaDetalhe === "resumo" && (
                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white text-[11px]">
                      {linhasResumoSelecionada.map(([label, value]) => (
                        <div key={label} className="grid grid-cols-[1fr_1.15fr] items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0">
                          <span className="text-slate-500">{label}</span>
                          <span className="font-medium text-slate-800">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {abaDetalhe === "parcelas" && (
                    <div>
                      {selecionada.parcelas.length === 0 ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                          <div className="text-xs font-semibold text-amber-800">Aguardando parcelas</div>
                          <div className="mt-1 text-[11px] text-amber-700">O monitoramento começa depois do lançamento das parcelas remanescentes.</div>
                          <button type="button" onClick={() => abrirLancamentoParcelas(selecionada.lead)} className="mt-3 rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-violet-700">
                            Lançar parcelas
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[10px] font-medium text-slate-600">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                              Paga
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                              Atrasada
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-2.5 w-2.5 rounded-full bg-sky-500" />
                              A vencer
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
                              Cancelada
                            </span>
                          </div>

                          {selecionada.parcelas.map((parcela, index) => {
                            const paga =
                              norm(parcela?.status) === "paga" ||
                              Boolean(parcela?.receb_lead_id);
                            const vencimentoParcela = dataYmd(
                              parcela?.vencimento || parcela?.data_vencimento,
                            );
                            const diasParcela = vencimentoParcela
                              ? diasEntre(hojeYmd(), vencimentoParcela)
                              : null;
                            const cancelada =
                              selecionada.status === "cancelado" && !paga;
                            const atrasada =
                              !paga && !cancelada && diasParcela !== null && diasParcela < 0;
                            const venceHoje =
                              !paga && !cancelada && diasParcela !== null && diasParcela === 0;

                            const visual = paga
                              ? {
                                  linha: "border-emerald-200 bg-emerald-50/90",
                                  barra: "bg-emerald-500",
                                  badge: "border-emerald-200 bg-white text-emerald-700",
                                  texto: "Paga",
                                }
                              : cancelada
                                ? {
                                    linha: "border-slate-300 bg-slate-100/90",
                                    barra: "bg-slate-500",
                                    badge: "border-slate-300 bg-white text-slate-700",
                                    texto: "Cancelada",
                                  }
                                : atrasada
                                  ? {
                                      linha: "border-rose-200 bg-rose-50/90",
                                      barra: "bg-rose-500",
                                      badge: "border-rose-200 bg-white text-rose-700",
                                      texto: `Atrasada ${Math.abs(diasParcela)}d`,
                                    }
                                  : venceHoje
                                    ? {
                                        linha: "border-amber-200 bg-amber-50/90",
                                        barra: "bg-amber-500",
                                        badge: "border-amber-200 bg-white text-amber-700",
                                        texto: "Vence hoje",
                                      }
                                    : {
                                        linha: "border-sky-200 bg-sky-50/60",
                                        barra: "bg-sky-500",
                                        badge: "border-sky-200 bg-white text-sky-700",
                                        texto: "A vencer",
                                      };

                            return (
                              <div
                                key={parcela?.id || index}
                                className={`relative overflow-hidden rounded-lg border p-2.5 text-[11px] ${visual.linha}`}
                              >
                                <span className={`absolute inset-y-0 left-0 w-1 ${visual.barra}`} />
                                <div className="grid grid-cols-[54px_minmax(0,1fr)_72px] items-center gap-2 pl-1.5">
                                  <div className="font-semibold text-slate-800">
                                    {parcela?.numero_parcela ?? parcela?.numero ?? index + 1}/
                                    {parcela?.total_parcelas ?? selecionada.parcelas.length}
                                  </div>

                                  <div className="min-w-0">
                                    <div className="font-medium text-slate-800">
                                      {formatarData(
                                        parcela?.vencimento || parcela?.data_vencimento,
                                      )}
                                    </div>
                                    {paga && parcela?.data_pagamento ? (
                                      <div className="mt-0.5 truncate text-[10px] text-emerald-700">
                                        Pago em {formatarData(parcela.data_pagamento)}
                                      </div>
                                    ) : cancelada ? (
                                      <div className="mt-0.5 text-[10px] text-slate-600">
                                        Encerrada em {formatarData(selecionada.cancelamento?.canceladoEm)}
                                      </div>
                                    ) : atrasada ? (
                                      <div className="mt-0.5 text-[10px] text-rose-700">
                                        {Math.abs(diasParcela)} dia(s) em atraso
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="text-right font-semibold text-slate-900">
                                    {moeda(parcela?.valor ?? parcela?.valor_boleto)}
                                  </div>
                                </div>

                                <div className="mt-2 flex items-center justify-end pl-1.5">
                                  <span
                                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${visual.badge}`}
                                  >
                                    {visual.texto}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {abaDetalhe === "tarefas" && (
                    <div className="space-y-2">
                      {tarefasSelecionada.length === 0 ? (
                        <div className="py-6 text-center text-[11px] text-slate-400">Nenhuma tarefa de cobrança registrada.</div>
                      ) : (
                        tarefasSelecionada.slice(0, 20).map((item, index) => (
                          <div key={item?.id || index} className="rounded-lg border border-slate-200 p-2">
                            <div className="text-[11px] font-medium text-slate-800">{textoEvento(item)}</div>
                            <div className="mt-1 text-[10px] text-slate-400">{formatarData(dataEvento(item))}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {abaDetalhe === "interacoes" && (
                    <div className="space-y-2">
                      {interacoesSelecionada.length === 0 ? (
                        <div className="py-6 text-center text-[11px] text-slate-400">Nenhuma interação de cobrança registrada.</div>
                      ) : (
                        [...interacoesSelecionada]
                          .sort((a, b) => String(dataEvento(b) || "").localeCompare(String(dataEvento(a) || "")))
                          .slice(0, 20)
                          .map((item, index) => (
                            <div key={item?.id || index} className="rounded-lg border border-slate-200 p-2">
                              <div className="text-[11px] font-medium text-slate-800">{textoEvento(item)}</div>
                              <div className="mt-1 text-[10px] text-slate-400">{formatarData(dataEvento(item))}</div>
                            </div>
                          ))
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t border-indigo-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-violet-50 p-3">
                  <div className="grid grid-cols-3 gap-2">
                    {selecionada.status === "aguardando" ? (
                      <button
                        type="button"
                        onClick={() => abrirLancamentoParcelas(selecionada.lead)}
                        className="col-span-1 flex h-10 items-center justify-center gap-1.5 rounded-xl border border-violet-500 bg-gradient-to-r from-violet-600 via-indigo-600 to-violet-600 px-3 text-[11px] font-semibold text-white shadow-sm transition-all hover:-translate-y-[1px] hover:shadow-md"
                      >
                        <PlusCircle className="h-3.5 w-3.5" />
                        <span>Lançar parcelas</span>
                      </button>
                    ) : selecionada.status === "cancelado" ? (
                      <button
                        type="button"
                        disabled
                        className="col-span-1 flex h-10 cursor-default items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-slate-100 px-3 text-[11px] font-semibold text-slate-700"
                      >
                        <X className="h-3.5 w-3.5" />
                        <span>Cancelado</span>
                      </button>
                    ) : selecionada.status === "pago" ? (
                      <button
                        type="button"
                        disabled
                        className="col-span-1 flex h-10 cursor-default items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-[11px] font-semibold text-emerald-700"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Quitado</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => abrirBaixaParcela(selecionada.lead, selecionada.parcelaAtual)}
                        className="col-span-1 flex h-10 items-center justify-center gap-1.5 rounded-xl border border-violet-500 bg-gradient-to-r from-violet-600 via-indigo-600 to-violet-600 px-3 text-[11px] font-semibold text-white shadow-sm transition-all hover:-translate-y-[1px] hover:shadow-md"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Dar baixa</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onInteracoes?.(selecionada.lead, contextoCobrancaSorriso(selecionada.lead, selecionada.parcelaAtual))}
                      className="flex h-10 items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-gradient-to-r from-white via-emerald-50 to-white px-3 text-[11px] font-semibold text-emerald-700 shadow-sm transition-all hover:-translate-y-[1px] hover:border-emerald-300 hover:shadow-md"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      <span>Interação</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onNovaTarefa?.(selecionada.lead, contextoCobrancaSorriso(selecionada.lead, selecionada.parcelaAtual))}
                      className="flex h-10 items-center justify-center gap-1.5 rounded-xl border border-amber-200 bg-gradient-to-r from-white via-amber-50 to-white px-3 text-[11px] font-semibold text-amber-700 shadow-sm transition-all hover:-translate-y-[1px] hover:border-amber-300 hover:shadow-md"
                    >
                      <ListTodo className="h-3.5 w-3.5" />
                      <span>Tarefa</span>
                    </button>
                  </div>

                  {gestor &&
                    selecionada.parcelas.length > 0 &&
                    !["aguardando", "pago", "cancelado"].includes(selecionada.status) && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => abrirCorrecaoLancamento(selecionada)}
                          className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 text-[11px] font-semibold text-amber-800 transition hover:bg-amber-100"
                          title="Corrigir dados lançados sem gerar cancelamento"
                        >
                          <PencilLine className="h-3.5 w-3.5" />
                          Corrigir lançamento
                        </button>
                        <button
                          type="button"
                          onClick={() => abrirCancelamento(selecionada)}
                          className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-3 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-100"
                        >
                          <X className="h-3.5 w-3.5" />
                          Cancelar plano
                        </button>
                      </div>
                    )}

                  <button type="button" onClick={() => onAbrirLead?.(selecionada.lead)} className="mt-2 flex w-full items-center justify-end gap-1 text-[11px] font-medium text-blue-700 hover:underline">
                    Ver lead completa <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </aside>
            )}
          </div>
        </section>

      {modalCorrecao && (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-slate-950/50 px-4 py-6">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-amber-200 bg-gradient-to-r from-amber-100 via-orange-50 to-yellow-50 px-5 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <PencilLine className="h-4 w-4 text-amber-700" />
                  <h3 className="text-base font-semibold text-slate-900">Corrigir lançamento</h3>
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  {modalCorrecao?.item?.lead?.nome_lead || "Paciente"} · Contrato #{modalCorrecao?.contratoId || "—"}
                </p>
              </div>
              <button
                type="button"
                disabled={salvandoCorrecao}
                onClick={() => setModalCorrecao(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700 disabled:opacity-50"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              {modalCorrecao?.parcelasPagas > 0 ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[12px] leading-relaxed text-emerald-900">
                  <strong>{modalCorrecao.parcelasPagas} parcela(s) já paga(s) estão protegidas.</strong>{" "}
                  Valor, vencimento, número da parcela, baixa e recebimento dessas parcelas não serão alterados.
                  A correção substituirá somente as parcelas futuras.
                </div>
              ) : (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-[12px] leading-relaxed text-blue-900">
                  Como ainda não existe pagamento, o lançamento pode ser corrigido por completo sem gerar cancelamento.
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] sm:grid-cols-4">
                <div>
                  <div className="uppercase tracking-[0.02em] text-slate-400">Pagas protegidas</div>
                  <div className="mt-1 text-sm font-semibold text-emerald-700">{modalCorrecao?.parcelasPagas || 0}</div>
                </div>
                <div>
                  <div className="uppercase tracking-[0.02em] text-slate-400">Futuras atuais</div>
                  <div className="mt-1 text-sm font-semibold text-slate-800">{modalCorrecao?.parcelasFuturasAtuais || 0}</div>
                </div>
                <div className="col-span-2">
                  <div className="uppercase tracking-[0.02em] text-slate-400">Valor futuro atual</div>
                  <div className="mt-1 text-sm font-semibold text-slate-800">{moeda(modalCorrecao?.valorFuturoAtual || 0)}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="text-xs font-medium text-slate-600">
                  Total de parcelas do contrato
                  <input
                    type="number"
                    min={modalCorrecao?.parcelasPagas > 0 ? modalCorrecao?.maxParcelaPaga || 1 : 1}
                    value={formCorrecao.total_parcelas}
                    disabled={salvandoCorrecao}
                    onChange={(e) => setFormCorrecao((prev) => ({ ...prev, total_parcelas: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  {modalCorrecao?.parcelasPagas > 0 ? "Próxima parcela protegida" : "Parcela inicial"}
                  <input
                    type="number"
                    min="1"
                    value={formCorrecao.parcela_inicial}
                    disabled={salvandoCorrecao || modalCorrecao?.parcelasPagas > 0}
                    onChange={(e) => setFormCorrecao((prev) => ({ ...prev, parcela_inicial: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-100 disabled:text-slate-500"
                  />
                  {modalCorrecao?.parcelasPagas > 0 && (
                    <span className="mt-1 block text-[10px] text-slate-400">
                      Definida automaticamente após a última parcela paga.
                    </span>
                  )}
                </label>

                <label className="text-xs font-medium text-slate-600">
                  {modalCorrecao?.parcelasPagas > 0 ? "Quantidade de parcelas futuras" : "Quantidade de parcelas"}
                  <input
                    type="number"
                    min="1"
                    value={formCorrecao.quantidade_parcelas}
                    disabled={salvandoCorrecao}
                    onChange={(e) => setFormCorrecao((prev) => ({ ...prev, quantidade_parcelas: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  {modalCorrecao?.parcelasPagas > 0 ? "Valor das parcelas futuras" : "Valor do boleto"}
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={formCorrecao.valor_boleto}
                    disabled={salvandoCorrecao}
                    onChange={(e) => setFormCorrecao((prev) => ({ ...prev, valor_boleto: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  {modalCorrecao?.parcelasPagas > 0 ? "Próximo vencimento" : "Primeiro vencimento"}
                  <input
                    type="date"
                    value={formCorrecao.primeiro_vencimento}
                    disabled={salvandoCorrecao}
                    onChange={(e) => setFormCorrecao((prev) => ({ ...prev, primeiro_vencimento: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  Captador da carteira
                  <select
                    value={formCorrecao.responsavel_id}
                    disabled={salvandoCorrecao}
                    onChange={(e) => setFormCorrecao((prev) => ({ ...prev, responsavel_id: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                  >
                    <option value="">Selecione</option>
                    {usuarios.map((u) => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block text-xs font-medium text-slate-600">
                Motivo da correção *
                <textarea
                  value={motivoCorrecao}
                  disabled={salvandoCorrecao}
                  onChange={(event) => setMotivoCorrecao(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Ex.: quantidade de parcelas lançada incorretamente. O correto são 8 parcelas de R$ 625,00."
                  className="mt-1 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                />
                <div className="mt-1 flex justify-between text-[10px] text-slate-400">
                  <span>Mínimo de 30 caracteres · obrigatório para auditoria</span>
                  <span>{String(motivoCorrecao || "").trim().length}/2000</span>
                </div>
              </label>

              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] leading-relaxed text-amber-900">
                Esta ação é uma <strong>correção operacional</strong>. Ela não cancela o plano e não entra nas estatísticas de cancelamento. O sistema guardará o antes e o depois da alteração.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                disabled={salvandoCorrecao}
                onClick={() => setModalCorrecao(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={
                  salvandoCorrecao ||
                  String(motivoCorrecao || "").trim().length < 30
                }
                onClick={salvarCorrecaoLancamento}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PencilLine className="h-4 w-4" />
                {salvandoCorrecao ? "Corrigindo..." : "Confirmar correção"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalCancelamento && (
        <div className="fixed inset-0 z-[10030] flex items-center justify-center bg-slate-950/50 px-4 py-6">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-rose-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-rose-100 bg-rose-50/70 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Cancelar Sorriso Planejado</h3>
                <p className="mt-1 text-xs text-slate-600">
                  {modalCancelamento?.item?.lead?.nome_lead || "Paciente"} · Contrato #{modalCancelamento?.contratoId || "—"}
                </p>
              </div>
              <button
                type="button"
                disabled={salvandoCancelamento}
                onClick={() => setModalCancelamento(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700 disabled:opacity-50"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.02em] text-slate-400">Pago</div>
                  <div className="mt-1 text-sm font-semibold text-emerald-700">
                    {moeda(modalCancelamento?.valorPago || 0)}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.02em] text-slate-400">Parcelas pagas</div>
                  <div className="mt-1 text-sm font-semibold text-slate-800">
                    {modalCancelamento?.parcelasPagas || 0}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.02em] text-slate-400">A encerrar</div>
                  <div className="mt-1 text-sm font-semibold text-slate-800">
                    {modalCancelamento?.parcelasEncerrar || 0}
                  </div>
                </div>
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.02em] text-rose-500">Saldo encerrado</div>
                  <div className="mt-1 text-sm font-semibold text-rose-800">
                    {moeda(modalCancelamento?.valorEncerrado || 0)}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[12px] text-slate-600">
                Classificação automática:{" "}
                <span className="font-semibold text-slate-900">
                  {modalCancelamento?.parcelasPagas > 0
                    ? "Cancelado após início"
                    : "Cancelado sem pagamento"}
                </span>
              </div>

              <label className="block text-xs font-medium text-slate-600">
                Motivo do cancelamento *
                <select
                  value={motivoCancelamento}
                  disabled={salvandoCancelamento}
                  onChange={(event) => setMotivoCancelamento(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50"
                >
                  <option value="">Selecione</option>
                  {MOTIVOS_CANCELAMENTO.map(([codigo, rotulo]) => (
                    <option key={codigo} value={codigo}>{rotulo}</option>
                  ))}
                </select>
              </label>

              <label className="block text-xs font-medium text-slate-600">
                Descrição do cancelamento *
                <textarea
                  value={descricaoCancelamento}
                  disabled={salvandoCancelamento}
                  onChange={(event) => setDescricaoCancelamento(event.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="Explique o que aconteceu, o que foi combinado com o paciente e por que o plano está sendo encerrado."
                  className="mt-1 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50"
                />
                <div className="mt-1 flex justify-between text-[10px] text-slate-400">
                  <span>Mínimo de 30 caracteres</span>
                  <span>{String(descricaoCancelamento || "").trim().length}/1000</span>
                </div>
              </label>

              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] leading-relaxed text-amber-900">
                O cancelamento preserva todas as parcelas já pagas e encerra as parcelas futuras desta carteira. Nenhum recebimento em receb_leads será apagado.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                disabled={salvandoCancelamento}
                onClick={() => setModalCancelamento(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={
                  salvandoCancelamento ||
                  !motivoCancelamento ||
                  String(descricaoCancelamento || "").trim().length < 30
                }
                onClick={salvarCancelamento}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                {salvandoCancelamento ? "Cancelando..." : "Confirmar cancelamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalBaixa && (
        <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/45 px-4 py-6">
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Dar baixa na parcela</h3>
                <p className="mt-1 text-xs text-slate-500">
                  {modalBaixa?.lead?.nome_lead || "Paciente"} · Parcela {modalBaixa?.parcela?.numero_parcela ?? "—"}/{modalBaixa?.parcela?.total_parcelas ?? "—"}
                </p>
              </div>
              <button
                type="button"
                disabled={salvandoBaixa}
                onClick={() => setModalBaixa(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.02em] text-slate-400">Vencimento</div>
                  <div className="mt-1 font-semibold text-slate-800">{formatarData(modalBaixa?.parcela?.vencimento || modalBaixa?.parcela?.data_vencimento)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.02em] text-slate-400">Valor</div>
                  <div className="mt-1 text-base font-semibold text-slate-900">{moeda(modalBaixa?.parcela?.valor ?? modalBaixa?.parcela?.valor_boleto)}</div>
                </div>
              </div>

              <label className="block text-xs font-medium text-slate-600">
                Data do recebimento
                <input
                  type="date"
                  value={dataBaixa}
                  disabled={salvandoBaixa}
                  onChange={(event) => setDataBaixa(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                />
              </label>

              <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-[12px] leading-relaxed text-blue-800">
                Ao confirmar, o valor será gravado em receb_leads e esta parcela ficará vinculada ao recebimento criado.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                disabled={salvandoBaixa}
                onClick={() => setModalBaixa(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={salvandoBaixa}
                onClick={salvarBaixaParcela}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-800 disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" />
                {salvandoBaixa ? "Salvando..." : "Confirmar baixa"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalParcelasLead && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/45 px-4 py-6">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Lançar parcelas</h3>
                <p className="mt-1 text-xs text-slate-500">
                  {modalParcelasLead?.nome_lead || "Paciente"} • Lead #{modalParcelasLead?.id}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !salvandoParcelas && setModalParcelasLead(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => {
                    setTipoContrato("novo");
                    setFormParcelas((prev) => ({ ...prev, parcela_inicial: "1", total_parcelas: prev.quantidade_parcelas || "" }));
                  }}
                  className={`rounded-lg px-3 py-2 text-xs font-medium transition ${
                    tipoContrato === "novo"
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Contrato novo
                </button>
                <button
                  type="button"
                  onClick={() => setTipoContrato("existente")}
                  className={`rounded-lg px-3 py-2 text-xs font-medium transition ${
                    tipoContrato === "existente"
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Contrato existente
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {tipoContrato === "existente" && (
                  <>
                    <label className="text-xs font-medium text-slate-600">
                      Total original de parcelas
                      <input
                        type="number"
                        min="1"
                        value={formParcelas.total_parcelas}
                        onChange={(e) => setFormParcelas((prev) => ({ ...prev, total_parcelas: e.target.value }))}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                    <label className="text-xs font-medium text-slate-600">
                      Próxima parcela
                      <input
                        type="number"
                        min="1"
                        value={formParcelas.parcela_inicial}
                        onChange={(e) => setFormParcelas((prev) => ({ ...prev, parcela_inicial: e.target.value }))}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                  </>
                )}

                <label className="text-xs font-medium text-slate-600">
                  {tipoContrato === "novo" ? "Quantidade de parcelas" : "Parcelas restantes"}
                  <input
                    type="number"
                    min="1"
                    value={formParcelas.quantidade_parcelas}
                    onChange={(e) =>
                      setFormParcelas((prev) => ({
                        ...prev,
                        quantidade_parcelas: e.target.value,
                        ...(tipoContrato === "novo" ? { total_parcelas: e.target.value, parcela_inicial: "1" } : {}),
                      }))
                    }
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  Valor do boleto
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={formParcelas.valor_boleto}
                    onChange={(e) => setFormParcelas((prev) => ({ ...prev, valor_boleto: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  {tipoContrato === "novo" ? "Primeiro vencimento" : "Próximo vencimento"}
                  <input
                    type="date"
                    value={formParcelas.primeiro_vencimento}
                    onChange={(e) => setFormParcelas((prev) => ({ ...prev, primeiro_vencimento: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>

                <label className="text-xs font-medium text-slate-600">
                  Captador da carteira
                  <select
                    value={formParcelas.responsavel_id}
                    disabled={!gestor}
                    onChange={(e) => setFormParcelas((prev) => ({ ...prev, responsavel_id: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                  >
                    <option value="">Selecione</option>
                    {(gestor ? usuarios : usuarios.filter((u) => String(u?.id) === String(usuarioLogado?.id))).map((u) => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-[12px] leading-relaxed text-blue-800">
                Depois de confirmar, as parcelas passam a alimentar previsto do mês, atrasos, inadimplência e a fila de cobrança desta carteira.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                disabled={salvandoParcelas}
                onClick={() => setModalParcelasLead(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={salvandoParcelas}
                onClick={salvarLancamentoParcelas}
                className="rounded-lg bg-blue-700 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-800 disabled:opacity-50"
              >
                {salvandoParcelas ? "Salvando..." : "Confirmar parcelas"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
